/**
 * Mock graph data — based on Wave 1 + Wave 2 reading of ^-marked sections.
 * Author-only view, scratch shape (will be modeled properly later).
 */

export type NodeType = 'Section' | 'Person' | 'Place' | 'Concept' | 'Motif' | 'Moment' | 'Source' | 'Arc' | 'SourceFile'

export type EdgeType = 'mentions' | 'echoes' | 'compresses_into' | 'in_arc' | 'in_source'

export type Stage = 'childhood' | 'adolescence-army-studies' | 'mitzpe-yaara' | 'opening' | 'synthesis' | 'meta'

export type SourceBucket = 'life' | 'tikkun' | 'destination'

export const STAGE_LABEL: Record<Stage, string> = {
  childhood: 'ילדות',
  'adolescence-army-studies': 'תיכון, צבא ולימודים',
  'mitzpe-yaara': 'מצפה ויערה',
  opening: 'הפתיחה',
  synthesis: 'סינתזה',
  meta: 'מתוך־כתיבה',
}

export const SOURCE_BUCKET_LABEL: Record<SourceBucket, string> = {
  life: 'חיים',
  tikkun: 'תיקון',
  destination: 'יעד',
}

export type Claimant = 'yoav' | 'claude' | 'both'

export type Status = 'proposed' | 'confirmed'

export interface GraphNode {
  id: string
  type: NodeType
  label: string
  attrs?: Record<string, string>
}

export interface GraphEdge {
  id: string
  sourceId: string
  targetId: string
  type: EdgeType
  gloss?: string
  claimant: Claimant
  status: Status
  /** false for symmetric relations (echoes) — direction-agnostic in UI */
  directional: boolean
}

export const NODE_TYPE_LABEL: Record<NodeType, string> = {
  Section: 'סקשן',
  Person: 'דמות',
  Place: 'מקום',
  Concept: 'רעיון',
  Motif: 'מוטיב',
  Moment: 'רגע',
  Source: 'מקור',
  Arc: 'ציר',
  SourceFile: 'קובץ מקור',
}

export const EDGE_TYPE_LABEL: Record<EdgeType, string> = {
  mentions: 'מזכיר',
  echoes: 'מהדהד',
  compresses_into: 'מתדחס אל',
  in_arc: 'בציר',
  in_source: 'בקובץ',
}

const nodes: GraphNode[] = [
  // Sections
  {
    id: 'section:vns47',
    type: 'Section',
    label: '^הִנֵּה לָכֶם מָנִיפֶסְט',
    attrs: { chapter: '1', source: 'ג׳קי-וזכרונות-ילדות' },
  },
  {
    id: 'section:uwe6h',
    type: 'Section',
    label: '^גלגול נשמות',
    attrs: { chapter: '2', source: 'ג׳קי-וזכרונות-ילדות' },
  },
  {
    id: 'section:k4sm6',
    type: 'Section',
    label: '^צְעָדִים רִאשׁוֹנִים',
    attrs: { chapter: '1', source: 'הורות,-ילדות' },
  },
  { id: 'section:5t3n9', type: 'Section', label: '^תַּצִּיג אֶת־עַצְמְךָ', attrs: { chapter: '1', source: 'אני' } },
  {
    id: 'section:bamju',
    type: 'Section',
    label: '^אָסוּר לִקְפּוֹץ מֵהַגֶּשֶׁר',
    attrs: { chapter: '1', source: 'אני' },
  },
  {
    id: 'section:ex4uc',
    type: 'Section',
    label: '^עוֹד לֹא חוֹדֶשׁ בָּעוֹלָם הַזֶּה',
    attrs: { chapter: '6', source: 'אני' },
  },
  { id: 'section:l5dr6', type: 'Section', label: '^מונופול', attrs: { chapter: '2', source: 'הנדסה' } },
  {
    id: 'section:v37cy',
    type: 'Section',
    label: '^הכי רחוק שניתן לדמיין',
    attrs: { chapter: '2', source: 'הורות,-ילדות' },
  },
  {
    id: 'section:1tye1',
    type: 'Section',
    label: '^הפסיפס שנחשף בלוד',
    attrs: { chapter: 'צאצאים יקרים', source: 'הורות,-ילדות' },
  },
  { id: 'section:b9y06', type: 'Section', label: '^על מה אתה כותב?', attrs: { chapter: '4', source: 'כתיבה' } },
  { id: 'section:34g10', type: 'Section', label: '^באתי להתלונן', attrs: { chapter: '21', source: 'כתיבה' } },
  { id: 'section:c1xqj', type: 'Section', label: '^רץ עם הטקסט', attrs: { chapter: '23', source: 'הורות,-ילדות' } },
  { id: 'section:z58pz', type: 'Section', label: '^למרד שתי צורות', attrs: { chapter: '24', source: 'המניפסט-הגלוי' } },
  {
    id: 'section:2f3gy',
    type: 'Section',
    label: '^המניפסט האחרון',
    attrs: { chapter: '25', source: 'המניפסט-האחרון', encrypted: 'true' },
  },

  // Persons
  { id: 'person:jackie', type: 'Person', label: 'ג׳קי' },
  { id: 'person:teacher', type: 'Person', label: '״המורה״', attrs: { kind: 'דמות־דיאלוג חוזרת' } },
  { id: 'person:dani', type: 'Person', label: 'דני' },
  { id: 'person:nelly', type: 'Person', label: 'נלי' },
  { id: 'person:yaara', type: 'Person', label: 'יערה' },
  { id: 'person:krishnamurti', type: 'Person', label: 'יידו קרישנמורטי' },
  { id: 'person:grossman', type: 'Person', label: 'דויד גרוסמן' },
  { id: 'person:rubinstein', type: 'Person', label: 'ארתור רובינשטיין' },
  { id: 'person:thiel', type: 'Person', label: 'פיטר ת׳יל' },
  { id: 'person:swift', type: 'Person', label: 'טיילור סוויפט' },
  { id: 'person:grandfather', type: 'Person', label: 'הסבא (אנונימי)' },

  // Places
  { id: 'place:field', type: 'Place', label: 'השדה' },
  { id: 'place:bridge', type: 'Place', label: 'הגשר' },
  { id: 'place:beach', type: 'Place', label: 'חוף הים' },
  { id: 'place:lod', type: 'Place', label: 'לוד' },
  { id: 'place:eilat', type: 'Place', label: 'אילת' },
  { id: 'place:tel-aviv', type: 'Place', label: 'תל אביב' },

  // Concepts
  { id: 'concept:recognition', type: 'Concept', label: 'הכרה' },
  { id: 'concept:moral-math', type: 'Concept', label: '״לא מסתדר מתמטית״' },
  { id: 'concept:fermi', type: 'Concept', label: 'פרדוקס פרמי' },
  { id: 'concept:universal-compassion', type: 'Concept', label: 'חמלה אוניברסלית' },
  { id: 'concept:psychological-revolt', type: 'Concept', label: 'מרד פסיכולוגי' },
  { id: 'concept:intergenerational-paradox', type: 'Concept', label: 'הפרדוקס הבין־דורי' },
  { id: 'concept:protagonist-bug', type: 'Concept', label: 'בעיית הפרוטגוניזם / סטוריטלינג' },
  { id: 'concept:perfect-grace', type: 'Concept', label: 'החסד המושלם' },
  { id: 'concept:distributed-algorithm', type: 'Concept', label: 'אלגוריתם מבוזר' },

  // Motifs
  { id: 'motif:button', type: 'Motif', label: 'הכפתור' },
  { id: 'motif:bird-in-shoebox', type: 'Motif', label: 'ציפור בקופסת נעליים' },
  { id: 'motif:bird-release', type: 'Motif', label: 'שחרור הציפור' },
  { id: 'motif:mosaic', type: 'Motif', label: 'הפסיפס בלוד' },
  { id: 'motif:thin-seam', type: 'Motif', label: '״התפר הדק בין החול והמים״' },

  // Moments
  { id: 'moment:opened-door', type: 'Moment', label: '״פתחתי את הדלת״' },
  { id: 'moment:bug-fixed-next', type: 'Moment', label: '״באג ראשון תוקן. נקסט.״' },
  { id: 'moment:would-you-press', type: 'Moment', label: '״הייתם לוחצים?״' },
  { id: 'moment:lift-your-head', type: 'Moment', label: '״תרימו את הראש״' },
  { id: 'moment:perfect-grace-line', type: 'Moment', label: '״מה שתמיד דמיינו כרע המוחלט הוא בעצם החסד המושלם״' },
  { id: 'moment:doesnt-add-up', type: 'Moment', label: '״זה פשוט לא מסתדר מתמטית״' },
  { id: 'moment:manifesto-mosaic', type: 'Moment', label: '״המניפסט הוא הפסיפס. והנה הן טביעות הרגליים״' },
  { id: 'moment:still-exist', type: 'Moment', label: '״אנחנו בכלל עוד קיימים?״' },
  { id: 'moment:never-asked-baby', type: 'Moment', label: '״מעולם עוד לא נולד הייצור שנשאל מראש אם הוא בכלל מעוניין״' },
  { id: 'moment:complain', type: 'Moment', label: '״באתי להתלונן״' },
  { id: 'moment:story-betrayal', type: 'Moment', label: '״סיפור הוא מעשה רמייה, אתם גורמים לנו לבגוד בעצמנו״' },
  { id: 'moment:i-am-frustrated', type: 'Moment', label: '״אני מתוסכל מכם כבר הרבה מאוד זמן״' },
  { id: 'moment:last-train', type: 'Moment', label: '״אתה רץ כאילו זו הרכבת האחרונה / היא לא״' },
  { id: 'moment:courage-sacrifice', type: 'Moment', label: '״מה שיבדיל בינינו הוא אומץ הלב, מידת ההקרבה״' },

  // Sources
  { id: 'source:communist-manifesto', type: 'Source', label: 'המניפסט הקומוניסטי' },
  { id: 'source:krishnamurti-quote', type: 'Source', label: 'ציטוט קרישנמורטי (״למרד שתי צורות״)' },
  { id: 'source:grossman-erasmus', type: 'Source', label: 'נאום גרוסמן (פרס ארסמוס)' },
  { id: 'source:rubinstein-quote', type: 'Source', label: 'ציטוט רובינשטיין' },
  { id: 'source:thiel-book', type: 'Source', label: 'הספר של ת׳יל' },
  { id: 'source:strangest-stories', type: 'Source', label: '״הסיפורים המוזרים ביותר בעולם״' },

  // Arcs
  { id: 'arc:fatherhood', type: 'Arc', label: 'אבהות' },
  { id: 'arc:masculinity', type: 'Arc', label: 'גבריות' },
  { id: 'arc:tikkun-olam', type: 'Arc', label: 'תיקון עולם' },
  { id: 'arc:coupledom', type: 'Arc', label: 'זוגיות' },
  { id: 'arc:homeland', type: 'Arc', label: 'מולדת' },

  // --- Wave 3 additions ---

  // Sections
  { id: 'section:ipv31', type: 'Section', label: '^מחשבית', attrs: { chapter: '3', source: 'הנדסה' } },
  { id: 'section:pl881', type: 'Section', label: '^פגישת מיתוג', attrs: { chapter: '12', source: 'הנדסה' } },
  { id: 'section:fvrmk', type: 'Section', label: '^ביובית', attrs: { chapter: '3', source: 'החברה-הישראלית' } },
  { id: 'section:9smx7', type: 'Section', label: '^ישן בסלון', attrs: { source: 'גבריות' } },
  {
    id: 'section:vq9l7',
    type: 'Section',
    label: '^השראה בדרך לעבודה',
    attrs: { chapter: '4', source: 'משרד,-עבודה,-קריירה' },
  },
  { id: 'section:nfzy3', type: 'Section', label: '^2000', attrs: { chapter: 'צאצאים יקרים', source: 'הורות,-ילדות' } },

  // Persons
  { id: 'person:mahshevet', type: 'Person', label: 'מחשבית', attrs: { kind: 'AI / דמות־דיאלוג' } },
  { id: 'person:eran', type: 'Person', label: 'ערן' },
  { id: 'person:bar', type: 'Person', label: 'בר' },
  { id: 'person:oded', type: 'Person', label: 'עודד' },
  { id: 'person:designer', type: 'Person', label: 'המעצבת', attrs: { kind: 'דמות־דיאלוג' } },
  { id: 'person:arab-worker', type: 'Person', label: 'העובד עם הכיפה' },
  { id: 'person:ofer', type: 'Person', label: 'עופר' },

  // Places
  { id: 'place:austria', type: 'Place', label: 'אוסטריה' },

  // Concepts
  { id: 'concept:suffering-reduction', type: 'Concept', label: 'הפחתת הסבל' },
  { id: 'concept:half-baked', type: 'Concept', label: '״נולדים חצי אפויים״' },
  { id: 'concept:encryption-as-theme', type: 'Concept', label: 'הצפנה (כתמה)' },
  { id: 'concept:assimilation-math', type: 'Concept', label: 'מתמטיקה של היטמעות' },

  // Motifs
  { id: 'motif:jar', type: 'Motif', label: 'הצנצנת (״הדברים הטובים״)' },
  { id: 'motif:creaky-door', type: 'Motif', label: 'הדלת החורקת' },
  { id: 'motif:kippah', type: 'Motif', label: 'הכיפה' },

  // Moments
  { id: 'moment:reduce-suffering', type: 'Moment', label: '״הבנת מחשב? הפחתת הסבל. זה הנושא כאן.״' },
  { id: 'moment:half-baked', type: 'Moment', label: '״אנחנו נולדים חצי אפויים״' },
  { id: 'moment:advance-paid', type: 'Moment', label: '״כבר שילמת לי מקדמה, נכון?״' },
  { id: 'moment:define-enemy', type: 'Moment', label: '״עכשיו תגדירו ׳אויב׳״' },
  { id: 'moment:appreciation', type: 'Moment', label: '״שאני מוקיר... הייתה לנו כזאת זוגיות נפלאה״' },
  { id: 'moment:eilat-finish', type: 'Moment', label: '״אם הייתי ממשיך עד לאילת הייתי מסיים את הספר״' },

  // Sources
  {
    id: 'source:classic-manifestos',
    type: 'Source',
    label: 'קלאסיקות המניפסטים (האדם, הזכויות, אמסטרדם, הפשיסטי, הסוריאליסטי)',
  },

  // --- Wave 4 additions ---

  // Sections
  { id: 'section:xwgu8', type: 'Section', label: '^דלת חורקת', attrs: { chapter: '3', source: 'המניפסט-הגלוי' } },
  { id: 'section:vkcgj', type: 'Section', label: '^תחליף שמות', attrs: { chapter: '3', source: 'כתיבה' } },
  { id: 'section:y6rx4', type: 'Section', label: '^אצל בר, עם עודד', attrs: { chapter: '6', source: 'חברים' } },
  {
    id: 'section:odokn',
    type: 'Section',
    label: '^מעמד הר סיני',
    attrs: { chapter: 'צאצאים יקרים', source: 'הורות,-ילדות' },
  },
  { id: 'section:kbstp', type: 'Section', label: '^הסיכה', attrs: { chapter: 'צאצאים יקרים', source: 'הורות,-ילדות' } },
  { id: 'section:adkli', type: 'Section', label: '^עודר', attrs: { source: 'הנדסה' } },
  { id: 'section:rb87n', type: 'Section', label: '^דרושה פיזיקאית', attrs: { source: 'הנדסה' } },
  { id: 'section:0sil4', type: 'Section', label: '^את יודעת', attrs: { source: 'גבריות' } },

  // Persons
  { id: 'person:akiva', type: 'Person', label: 'רבי עקיבא' },
  { id: 'person:fizikait', type: 'Person', label: 'הפיזיקאית', attrs: { kind: 'דמות־דיאלוג' } },
  { id: 'person:tamar', type: 'Person', label: 'סבתא תמר וקסלר' },

  // Places
  { id: 'place:barcelona', type: 'Place', label: 'ברצלונה' },
  { id: 'place:netherlands', type: 'Place', label: 'הולנד' },

  // Motifs
  { id: 'motif:mosquitoes', type: 'Motif', label: 'יתושים' },
  { id: 'motif:pin', type: 'Motif', label: 'הסיכה (ירושה משפחתית)' },
  { id: 'motif:compass', type: 'Motif', label: 'מצפן (״מניפסטים הם סוג של מצפן״)' },

  // Moments
  {
    id: 'moment:door-arrived',
    type: 'Moment',
    label: '״כשהדלת של השכנים תפסיק לחרוק אני אדע — המניפסט האנושי האחרון הגיע עכשיו גם לכאן״',
  },
  { id: 'moment:names-same', type: 'Moment', label: '״את יערה החלפתי ליערה, את דני החלפתי לדני״' },
  {
    id: 'moment:descendant-fates',
    type: 'Moment',
    label: '״מי מכם צאצאים יקרים יהיה אסיר פוליטי? מי יהיה מיליארדר? מי תהיה זונה?״',
  },
  {
    id: 'moment:mosquito-apology',
    type: 'Moment',
    label: '״סליחה על היתושים הלא מוצלחתים שהכחדתי, סליחה על המוצלחים שנתתי להם לחמוק״',
  },
  { id: 'moment:bring-it-to-read', type: 'Moment', label: '״תביא, אני רוצה לקרוא״' },
  {
    id: 'moment:tear-compass',
    type: 'Moment',
    label: '״מניפסטים הם סוג של מצפן... ויש את העיניים שלך, עם הדמעה הזאת, שתמיד מצביעה לכיוון הנכון״',
  },
  {
    id: 'moment:reduce-suffering-purpose',
    type: 'Moment',
    label: '״הפחתתי כאב יותר משיצרתי. זה הטוב שאני מאמין בו, זו המשמעות של הקיום האנושי בעיני״',
  },

  // Sources
  {
    id: 'source:knesset-execution-report',
    type: 'Source',
    label: 'דו״ח הכנסת על שיטות הוצאה להורג (יוני גבאי, כיכר השבת)',
  },

  // --- Wave 5 additions ---

  // Sections
  { id: 'section:g1n8e', type: 'Section', label: '^הנאום של אליושה', attrs: { chapter: '22', source: 'הורות,-ילדות' } },
  { id: 'section:wjpfy', type: 'Section', label: '^אי שוויון', attrs: { source: 'הנדסה' } },
  { id: 'section:432rb', type: 'Section', label: '^פירמידות שלא ביקשנו לבנות', attrs: { source: 'הנדסה' } },
  { id: 'section:vy99k', type: 'Section', label: '^עם יערה, בשדה דב', attrs: { source: 'גבריות' } },
  { id: 'section:jdhee', type: 'Section', label: '^סיינפלד', attrs: { source: 'גבריות' } },
  { id: 'section:mp2l0', type: 'Section', label: '^מה מביא אותך אלי?', attrs: { source: 'פסיכולוג' } },
  { id: 'section:hhsas', type: 'Section', label: '^על האש אצל דייב', attrs: { source: 'טבעונות' } },
  { id: 'section:pzxl2', type: 'Section', label: '^הנדסה קיומית', attrs: { source: 'הנדסה' } },

  // Persons
  { id: 'person:psychologist', type: 'Person', label: 'הפסיכולוגית', attrs: { kind: 'דמות־דיאלוג' } },
  { id: 'person:grandpa-yossi', type: 'Person', label: 'סבא יוסי' },
  { id: 'person:dave', type: 'Person', label: 'דייב' },
  { id: 'person:gaza-girl', type: 'Person', label: 'ילדה בת 5 מעזה (אנונימית)' },
  { id: 'person:schopenhauer', type: 'Person', label: 'שופנהאואר' },
  { id: 'person:aliosha', type: 'Person', label: 'אליושה (קרמזוב)' },
  { id: 'person:seinfeld', type: 'Person', label: 'ג׳רי סיינפלד' },

  // Places
  { id: 'place:yafo', type: 'Place', label: 'יפו' },
  { id: 'place:gaza', type: 'Place', label: 'עזה' },
  { id: 'place:sde-dov', type: 'Place', label: 'שדה דב' },

  // Concepts
  { id: 'concept:existential-engineering', type: 'Concept', label: 'הנדסה קיומית (Existential engineering)' },

  // Motifs
  { id: 'motif:pyramids', type: 'Motif', label: 'הפירמידות (שלא ביקשנו לבנות)' },
  { id: 'motif:fishing', type: 'Motif', label: 'הדיג' },
  { id: 'motif:meat', type: 'Motif', label: 'בשר / על האש' },

  // Sources
  { id: 'source:karamazov-aliosha', type: 'Source', label: 'נאום אליושה (קרמזוב)' },
  { id: 'source:schopenhauer', type: 'Source', label: 'שופנהאואר' },
  { id: 'source:armistice-1918', type: 'Source', label: 'הפסקת האש 11/11/1918' },

  // Moments
  {
    id: 'moment:pyramid-uninvited',
    type: 'Moment',
    label: '״כולנו שוכנים בתוך פירמידות שלא ביקשנו לבנות ושלתחזוקן אנחנו משועבדים״',
  },
  { id: 'moment:terrorist-explanation', type: 'Moment', label: '״אנשים שלא הסבירו את עצמם מספיק טוב״' },
  {
    id: 'moment:fisherman-math',
    type: 'Moment',
    label: '״אם מחברים את ההתרגשות של הדייג והבעתה של הדג יוצא מספר שלילי״',
  },
  { id: 'moment:break-the-pyramid', type: 'Moment', label: '״בראש הפירמידה צריך לעמוד בנאדם שרוצה לשבור את הפירמידה״' },
  { id: 'moment:armistice-deaths', type: 'Moment', label: '״המוות של השעות האלה זה כבר סתם״' },
  { id: 'moment:fear-disappearing', type: 'Moment', label: '״אני מפחד שבסוף אף אחד לא ישמע, אני מפחד להיעלם״' },

  // --- Wave 6 additions ---

  // Sections
  { id: 'section:el0pb', type: 'Section', label: '^מציג את עצמי - 1', attrs: { source: 'אני' } },
  { id: 'section:5l706', type: 'Section', label: '^למה את עצובה', attrs: { source: 'אני' } },
  { id: 'section:lvq6c', type: 'Section', label: '^מקלחת', attrs: { source: 'אני' } },
  { id: 'section:kql06', type: 'Section', label: '^מלון האינסוף', attrs: { source: 'הנדסה' } },
  { id: 'section:qrd6i', type: 'Section', label: '^פירמידה הפוכה', attrs: { source: 'הנדסה' } },
  { id: 'section:z95et', type: 'Section', label: '^עם הזמן למדתי', attrs: { source: 'חברים' } },
  { id: 'section:6zt53', type: 'Section', label: '^אני כמעט פציפיסט', attrs: { source: 'חברים' } },
  { id: 'section:vdugi', type: 'Section', label: '^לא היה סימן', attrs: { source: 'המניפסט-הגלוי' } },

  // Persons
  { id: 'person:saddam', type: 'Person', label: 'סדאם חוסיין' },
  { id: 'person:hilbert', type: 'Person', label: 'דוד הילברט' },
  { id: 'person:altman', type: 'Person', label: 'סם אלטמן' },

  // Motifs
  { id: 'motif:saddam-hall', type: 'Motif', label: 'האולם של סדאם (בעיית התיאום)' },
  { id: 'motif:hot-spring', type: 'Motif', label: 'מעיין חם / מקלחת (מרחב התובנה)' },
  { id: 'motif:hilbert-hotel', type: 'Motif', label: 'מלון הילברט (האינסוף)' },

  // Concepts
  { id: 'concept:signal', type: 'Concept', label: 'הסימן (לפעולה קולקטיבית)' },

  // Sources
  { id: 'source:hilbert-hotel', type: 'Source', label: 'מלון הילברט (ניסוי מחשבתי)' },

  // Moments
  {
    id: 'moment:saddam-mustache',
    type: 'Moment',
    label: '״אם היינו קמים כולנו ביחד היינו אוכלים אותו. רק השפם היה נשאר על הרצפה״',
  },
  { id: 'moment:no-signal', type: 'Moment', label: '״לא היה סימן״' },
  { id: 'moment:sad-face-superpower', type: 'Moment', label: '״לכל ילד בכיתה היה איזה כוח על... לי היה פרצוף עצוב״' },
  {
    id: 'moment:slow-heartbeat',
    type: 'Moment',
    label: '״אני מאט את קצב פעימות הלב, עוצר את חילוף החומרים. ממתין לאביב שיגיע״',
  },
  {
    id: 'moment:terrorist-pacifist',
    type: 'Moment',
    label: '״אני טרוריסט בסופו של דבר. מאמין בפציפיזם כמעט באופן מוחלט״',
  },
  { id: 'moment:billionaire-no-right', type: 'Moment', label: '״אין למיליארדר יותר זכות קיום בעולם שלנו״' },

  // Arcs
  { id: 'arc:pacifism', type: 'Arc', label: 'פציפיזם' },

  // --- Wave 7 additions ---

  // Sections
  { id: 'section:o3scq', type: 'Section', label: '^האורגיה', attrs: { source: 'גבריות' } },
  { id: 'section:0vjhk', type: 'Section', label: '^תתחילי בעמוד 38', attrs: { source: 'גבריות' } },
  { id: 'section:o5fsv', type: 'Section', label: '^סליחה, ערב טוב', attrs: { source: 'גבריות' } },
  { id: 'section:cmsiv', type: 'Section', label: '^לא יכול היה להיות סידור אחר', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:wii5l', type: 'Section', label: '^יום השואה', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:fh2np', type: 'Section', label: '^יהדות כהשראה', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:mk79t', type: 'Section', label: '^אחי, רוצה להניח תפילין?', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:zza3z', type: 'Section', label: '^אם אי פעם תהיו במצב של מלחמה', attrs: { source: 'פניה-לאחרים' } },

  // Persons
  { id: 'person:nietzsche', type: 'Person', label: 'ניטשה' },
  { id: 'person:dostoyevsky', type: 'Person', label: 'דוסטוייבסקי' },
  { id: 'person:diana-hasson', type: 'Person', label: 'דיאנה חסון (קורבן שואה)' },

  // Places
  { id: 'place:rodi-italy', type: 'Place', label: 'רודי (איטליה)' },
  { id: 'place:auschwitz', type: 'Place', label: 'אושוויץ' },

  // Concepts
  { id: 'concept:bigger-tribe', type: 'Concept', label: 'שבט גדול יותר (אוניברסליות)' },
  { id: 'concept:pacifist-heroism', type: 'Concept', label: 'גבורה פציפיסטית' },

  // Motifs
  { id: 'motif:rifle', type: 'Motif', label: 'הרובה (כיחידת סבל)' },

  // Sources
  { id: 'source:nietzsche', type: 'Source', label: 'ניטשה — מסות על חינוך לתרבות' },

  // Moments
  { id: 'moment:aaron-hasson', type: 'Moment', label: '״ינאי אמר לי תראי, לי יש את אהרון חסון, אני אבא שלך״' },
  { id: 'moment:atheist-messiah', type: 'Moment', label: '״המשיח כבר בא והוא אתאיסט״' },
  { id: 'moment:rifle-suffering', type: 'Moment', label: '״כמה סבל רובה אחד מפחית בממוצע?״' },
  { id: 'moment:bigger-tribe', type: 'Moment', label: '״אני פשוט מבקש מכם לבחור בשבט יותר גדול״' },
  { id: 'moment:page-38', type: 'Moment', label: '״תתחילי בעמוד 38, אבל לא עכשיו, חכי שאתרחק״' },

  // --- Wave 8 additions ---

  // Sections
  { id: 'section:eu99q', type: 'Section', label: '^מחתרת', attrs: { source: 'אני' } },
  { id: 'section:f4cwz', type: 'Section', label: '^פרס נובל', attrs: { source: 'אני' } },
  { id: 'section:0k1m6', type: 'Section', label: '^תפרוס לי את זה', attrs: { source: 'גבריות' } },
  { id: 'section:j0p47', type: 'Section', label: '^אבא', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:qjxw2', type: 'Section', label: '^הצפה', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:6beuw', type: 'Section', label: '^יהדות - נאום תוכחה', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:7pxpc', type: 'Section', label: '^ממריא לאוסטרליה', attrs: { source: 'פניה-לאחרים' } },
  { id: 'section:26n6m', type: 'Section', label: '^מונולוג', attrs: { source: 'כתיבה' } },

  // Persons
  { id: 'person:plato', type: 'Person', label: 'אפלטון' },
  { id: 'person:bruno-amadio', type: 'Person', label: 'ברונו אמדיו (״הילד הבוכה״)' },
  { id: 'person:shlomi', type: 'Person', label: 'שלומי', attrs: { kind: 'דמות־דיאלוג' } },
  { id: 'person:dylan', type: 'Person', label: 'בוב דילן' },

  // Motifs
  { id: 'motif:crying-boy', type: 'Motif', label: 'הילד הבוכה (תמונת ברונו אמדיו)' },
  { id: 'motif:underground', type: 'Motif', label: 'המחתרת' },
  { id: 'motif:flood', type: 'Motif', label: 'ההצפה' },

  // Concepts
  { id: 'concept:king-vs-prophet', type: 'Concept', label: 'מלך מול נביא' },
  { id: 'concept:monologue-vs-dialogue', type: 'Concept', label: 'מונולוג מול דיאלוג' },

  // Moments
  {
    id: 'moment:lion-and-zebra',
    type: 'Moment',
    label: '״אנחנו היינו האריה שרצה ארוחה, והם יהיו הזברה, שהגיהינום שלה התרחש בגלל המשחק של שנינו״',
  },
  { id: 'moment:please-disconnect', type: 'Moment', label: '״ביקשת להתנתק... כל יום בחמשת החודשים האחרונים, אדוני״' },
  {
    id: 'moment:father-walks-away',
    type: 'Moment',
    label: '״הילד הזה ראה איך אבא שלו מביט בו בחיוך חסר אונים, משפיל עיניים, מפנה לו את הגב, ומתרחק״',
  },
  {
    id: 'moment:mufasa-quote',
    type: 'Moment',
    label: '״הוא עצוב כי לא היה לו הרבה זמן להיות עם אבא שלו״ (נלי על מופאסה)',
  },
  { id: 'moment:passport-israeli', type: 'Moment', label: '״אני ישראלי בדרכון, אבל לא בהתנהגות״' },
  { id: 'moment:wars-from-dialogue', type: 'Moment', label: '״מלחמות שהתחילו מהצורך שלנו בדיאלוגים״' },
  { id: 'moment:king-not-prophet', type: 'Moment', label: '״מלכים לא יכולים להיות נביאים. אתה זה הם״' },

  // --- Wave 9 additions ---

  // Sections
  { id: 'section:shg1c', type: 'Section', label: '^פצוע', attrs: { source: 'אני' } },
  { id: 'section:fjggr', type: 'Section', label: '^יש לך פייסל?', attrs: { source: 'אני' } },
  { id: 'section:6n3p1', type: 'Section', label: '^זיקנה', attrs: { source: 'גבריות' } },
  { id: 'section:3ycdx', type: 'Section', label: '^רוצה כיף', attrs: { source: 'גבריות' } },
  { id: 'section:6qpg1', type: 'Section', label: '^הייתי חלש', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:igclx', type: 'Section', label: '^הדבר הכי עצוב בעולם', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:hiw9o', type: 'Section', label: '^חלום: טיול שנתי', attrs: { source: 'חברים' } },
  { id: 'section:dzrho', type: 'Section', label: '^אני יודעת מי אתה', attrs: { source: 'פניה-לאחרים' } },

  // Places
  { id: 'place:sydney', type: 'Place', label: 'סידני' },

  // Motifs
  { id: 'motif:disappearance', type: 'Motif', label: 'היעלמות' },
  { id: 'motif:old-photographs', type: 'Motif', label: 'תמונות ישנות' },

  // Concepts
  { id: 'concept:born-from-weakness', type: 'Concept', label: 'הולדה מחולשה' },

  // Moments
  { id: 'moment:want-to-disappear', type: 'Moment', label: '״לפעמים אני פשוט רוצה להיעלם״' },
  { id: 'moment:turtle-faisal', type: 'Moment', label: '״יש לך פייסל?״' },
  { id: 'moment:born-weak', type: 'Moment', label: '״הבאתי אתכן לעולם כי הייתי חלש״' },
  { id: 'moment:i-know-who-you-are', type: 'Moment', label: '״אני יודעת מי אתה. אתה כתבת את המניפסט״' },
  {
    id: 'moment:tibetan-monks-snow',
    type: 'Moment',
    label: '״הנזירים הטיבטים שהלכו להתבודד בהרים, לשחרר את הנשמה בשלג״',
  },

  // --- Wave 10 additions ---

  // Sections
  { id: 'section:cnx02', type: 'Section', label: '^נרדם על הבטן', attrs: { source: 'אני' } },
  { id: 'section:1z89o', type: 'Section', label: '^דלקת חניכיים', attrs: { source: 'גבריות' } },
  { id: 'section:p93pj', type: 'Section', label: '^אחרי שגברת מס׳ 31 זרקה אותי', attrs: { source: 'גבריות' } },
  { id: 'section:8t1ae', type: 'Section', label: '^עם נלי בחוף נחשולים', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:hftnk', type: 'Section', label: '^בלוני הליום', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:th813', type: 'Section', label: '^חלקו לפיסקאות', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:c1s75', type: 'Section', label: '^נושאים לשיחה', attrs: { source: 'חברים' } },
  { id: 'section:8bnsj', type: 'Section', label: '^ארוחת צהריים במשרד - 2', attrs: { source: 'משרד,-עבודה,-קריירה' } },

  // Persons
  { id: 'person:michal-tania', type: 'Person', label: 'מיכל טניה', attrs: { kind: 'דמות־דיאלוג' } },
  { id: 'person:hegel', type: 'Person', label: 'הגל' },

  // Sources
  { id: 'source:hegel', type: 'Source', label: 'הגל — ההיסטוריה כהתקדמות־דיאלקטית' },

  // Moments
  {
    id: 'moment:nelly-fire-die',
    type: 'Moment',
    label: '״הייתי רוצה למות משריפה אם זה לא היה כואב, כי נראה לי שזה כיף שאתה מתפורר״ (נלי)',
  },
  {
    id: 'moment:speaking-to-vampires',
    type: 'Moment',
    label: '״עבר בי פחד שאולי אני כבר מדבר אל ערפדים. אני כבר לא מרגיש שייך לכאן יותר״',
  },
  {
    id: 'moment:planet-under-belly',
    type: 'Moment',
    label: '״כשאני לבד אני נרדם על הבטן, מחבק את כוכב הלכת שפרוס מתחתי״',
  },

  // --- Wave 11 additions ---

  // Sections
  { id: 'section:obmcs', type: 'Section', label: '^פעם כתבתי קצר', attrs: { source: 'אני' } },
  { id: 'section:3rcmm', type: 'Section', label: '^יער בן שמן', attrs: { source: 'גבריות' } },
  { id: 'section:k90lu', type: 'Section', label: '^דני ונלי רבות', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:sae6e', type: 'Section', label: '^הבטחה', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:9nez9', type: 'Section', label: '^גם אני הייתי נוקם', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:xfwea', type: 'Section', label: '^העם הכי מטומטם בהיסטוריה', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:pg4q6', type: 'Section', label: '^חוזר מבר, ריח של זפת', attrs: { source: 'חברים' } },
  { id: 'section:9ij28', type: 'Section', label: '^מחשבות על כתיבה', attrs: { source: 'כתיבה' } },

  // Concepts
  { id: 'concept:promised-death', type: 'Concept', label: 'הבטחה־מוות־ברישיון (אסיים רק אם תרשו)' },

  // Moments
  { id: 'moment:short-life-spans', type: 'Moment', label: '״אני לא אדם של שנים רבות״' },
  {
    id: 'moment:would-take-revenge',
    type: 'Moment',
    label: '״אני הייתי נוקם במוסדות ובאליטה בדיוק כפי שאתם עשיתם. יש בינינו הרבה יותר משותף מאשר בינינו למרכז״',
  },
  { id: 'moment:stupidest-nation', type: 'Moment', label: '״אנחנו העם הכי מטומטם בהיסטוריה״' },
  { id: 'moment:promise-to-release', type: 'Moment', label: '״אשאל אתכן אם אתן משחררות אותי״' },
  { id: 'moment:born-won-died', type: 'Moment', label: '״מי שנולד הרוויח, ומי שחי מת״' },

  // --- Wave 12 additions ---

  // Sections
  { id: 'section:f0gcr', type: 'Section', label: '^רינה', attrs: { chapter: '4', source: 'גבריות' } },
  { id: 'section:rlykw', type: 'Section', label: '^אנשים שאני אולי מכיר', attrs: { source: 'גבריות' } },
  { id: 'section:2m1t5', type: 'Section', label: '^השייט באילת', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:ciuso', type: 'Section', label: '^גנדהי סטייל', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:p95te', type: 'Section', label: '^שישי בבוקר בעיר', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:hhc3f', type: 'Section', label: '^זו טעות להאמין לאמנים', attrs: { source: 'הנדסה' } },
  { id: 'section:vo8g6', type: 'Section', label: '^רעב / חוסר', attrs: { chapter: '4', source: 'כתיבה' } },
  { id: 'section:k0ael', type: 'Section', label: '^אהה מומנט', attrs: { source: 'משרד,-עבודה,-קריירה' } },

  // Persons
  { id: 'person:rina', type: 'Person', label: 'רינה (בסמבטיון)' },
  { id: 'person:gandhi', type: 'Person', label: 'גנדהי' },

  // Moments
  { id: 'moment:half-married', type: 'Moment', label: '״להיות גבר לא לגמרי נשוי, אבל גם לא לגמרי פנוי״' },
  { id: 'moment:starved-vs-lacking', type: 'Moment', label: '״כלוא בין רעב לחוסר״' },
  { id: 'moment:protect-from-revolt', type: 'Moment', label: '״מי כי כל כך יקר לך שאתה לא מורד?״' },
  {
    id: 'moment:cleaners-bathroom',
    type: 'Moment',
    label: '״מעולם לא ראיתי את המנקים משתמשים בשירותים, הנה אהה מומנט״',
  },
  {
    id: 'moment:trust-failed-artists',
    type: 'Moment',
    label: '״האמנים היחידים שאפשר להאמין להם הם האלמוניים והכושלים. וזו בעיה, כי הם ממש גרועים״',
  },
  { id: 'moment:olive-branches-up', type: 'Moment', label: '״ידיים למעלה, עם עלי זית. לא דגל לבן, עם ענפים של זית״' },
  { id: 'moment:she-was-cub-too', type: 'Moment', label: '״פעם היא גם הייתה גורה״' },

  // --- Wave 13 additions ---

  // Sections
  { id: 'section:sixbu', type: 'Section', label: '^מכניס את הבטן', attrs: { source: 'אני' } },
  { id: 'section:6gb39', type: 'Section', label: '^פוסט פמיניזם ברחוב', attrs: { source: 'גבריות' } },
  { id: 'section:7af3y', type: 'Section', label: '^סויפט בוומבלי', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:hd9y9', type: 'Section', label: '^בורחים לסבא וסבתא', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:sd34h', type: 'Section', label: '^בסופר', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:7aynu', type: 'Section', label: '^עוד מישהו רוצה לשתף?', attrs: { source: 'פניה-לאחרים' } },
  { id: 'section:b1t5r', type: 'Section', label: '^סימטה אלמונית', attrs: { source: 'הנדסה' } },

  // Persons
  { id: 'person:musk', type: 'Person', label: 'אילון מאסק' },

  // Moments
  {
    id: 'moment:my-country-changing',
    type: 'Moment',
    label: '״המדינה שלי משתנה לי מול העיניים. זה גומר לי את הלב. פעם הייתי גאה. היום אני מתבייש בה נורא״',
  },
  {
    id: 'moment:streets-anonymous',
    type: 'Moment',
    label:
      '״רחובות צריכים להיקרא על שם אנשים אלמונים. המפורסמים כבר קיבלו מספיק תהילה בחייהם, שישחררו אותנו לפחות במותם״',
  },

  // --- Wave 14 additions ---

  // Sections
  { id: 'section:0ikrc', type: 'Section', label: '^מתארגנים לפיקניק', attrs: { source: 'הורות,-ילדות' } },
  { id: 'section:2vzzl', type: 'Section', label: '^ויזיט פלסטיין ודרך האמצע', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:m6lah', type: 'Section', label: '^סלקציה עצמית', attrs: { source: 'החברה-הישראלית' } },
  { id: 'section:lw4rh', type: 'Section', label: '^אני חכם, אבל צריך לדייק', attrs: { source: 'כתיבה' } },
  { id: 'section:yv95b', type: 'Section', label: '^למי אני כותב?', attrs: { source: 'כתיבה' } },
  { id: 'section:214hl', type: 'Section', label: '^פטריה עם עודד וחבר שלו אצלי בבית', attrs: { source: 'חברים' } },
  { id: 'section:g1is2', type: 'Section', label: '^הקדמה למהדורה הסינית', attrs: { source: 'פניה-לאחרים' } },

  // Persons
  { id: 'person:viktor-frankl', type: 'Person', label: 'ויקטור פרנקל' },

  // Places
  { id: 'place:china', type: 'Place', label: 'סין (צ׳נגדו)' },
  { id: 'place:jericho', type: 'Place', label: 'יריחו' },

  // Concepts
  { id: 'concept:gentle-perish', type: 'Concept', label: 'הסלקציה העצמית — העדינים נכחדים ראשונים' },
  { id: 'concept:courage-as-intelligence', type: 'Concept', label: 'אומץ לב כמדד האמיתי (במקום אינטליגנציה)' },

  // Sources
  { id: 'source:viktor-frankl', type: 'Source', label: 'ויקטור פרנקל — האדם מחפש משמעות' },

  // Moments
  { id: 'moment:non-suffered-people', type: 'Moment', label: '״יש עם שלא עבר זוועה?״' },
  { id: 'moment:best-not-back', type: 'Moment', label: '״הטובים שבנו לא חזרו״ (פרנקל)' },
  {
    id: 'moment:write-grand',
    type: 'Moment',
    label: '״הטקסט הזה נולד כדי לגעת במשהו נשגב. חייבים להאמין כדי לגעת בו״',
  },
  {
    id: 'moment:courage-do-right',
    type: 'Moment',
    label: '״ההבדל שאני מציע למדוד הוא מידת אומץ הלב לעשות את הדבר הנכון״',
  },

  // --- Wave 15 (final) additions ---

  // Sections
  { id: 'section:zf7ql', type: 'Section', label: '^לנין', attrs: { source: 'משרד,-עבודה,-קריירה' } },
  { id: 'section:8kjcg', type: 'Section', label: '^בקונקשן בפרנקפורט', attrs: { source: 'פניה-לאחרים' } },
  { id: 'section:wrrph', type: 'Section', label: '^קונה בורג', attrs: { source: 'הנדסה' } },

  // Persons
  { id: 'person:lenin', type: 'Person', label: 'ולדימיר לנין' },

  // Places
  { id: 'place:argentina', type: 'Place', label: 'ארגנטינה' },
  { id: 'place:buenos-aires', type: 'Place', label: 'בואנוס איירס' },

  // Sources
  {
    id: 'source:lenin-quote',
    type: 'Source',
    label: 'לנין — ״ישנם עשורים שבהם דבר לא קורה, וישנם שבועות שבהם קורים עשורים״',
  },

  // Moments
  {
    id: 'moment:communists-for-fundraising',
    type: 'Moment',
    label: '״דווקא את הקומוניסטים הם בסוף רותמים כדי לגייס לעצמם כספים״',
  },
  { id: 'moment:cant-price-yourself', type: 'Moment', label: '״ואללה, אתה לא יודע לתמחר את עצמך״' },

  // --- SourceFile nodes (14) — author's natural clustering ---

  // חיים (7)
  { id: 'source-file:ani', type: 'SourceFile', label: 'אני', attrs: { bucket: 'life' } },
  { id: 'source-file:jackie', type: 'SourceFile', label: 'ג׳קי וזכרונות ילדות', attrs: { bucket: 'life' } },
  { id: 'source-file:masculinity', type: 'SourceFile', label: 'גבריות', attrs: { bucket: 'life' } },
  { id: 'source-file:parenting', type: 'SourceFile', label: 'הורות, ילדות', attrs: { bucket: 'life' } },
  { id: 'source-file:friends', type: 'SourceFile', label: 'חברים', attrs: { bucket: 'life' } },
  { id: 'source-file:psychologist', type: 'SourceFile', label: 'פסיכולוג', attrs: { bucket: 'life' } },
  { id: 'source-file:office', type: 'SourceFile', label: 'משרד, עבודה, קריירה', attrs: { bucket: 'life' } },

  // תיקון (5)
  { id: 'source-file:writing', type: 'SourceFile', label: 'כתיבה', attrs: { bucket: 'tikkun' } },
  { id: 'source-file:engineering', type: 'SourceFile', label: 'הנדסה', attrs: { bucket: 'tikkun' } },
  { id: 'source-file:vegetarianism', type: 'SourceFile', label: 'טבעונות', attrs: { bucket: 'tikkun' } },
  { id: 'source-file:israeli-society', type: 'SourceFile', label: 'החברה הישראלית', attrs: { bucket: 'tikkun' } },
  { id: 'source-file:address', type: 'SourceFile', label: 'פניה לאחרים', attrs: { bucket: 'tikkun' } },

  // יעד (2)
  { id: 'source-file:manifesto-visible', type: 'SourceFile', label: 'המניפסט הגלוי', attrs: { bucket: 'destination' } },
  {
    id: 'source-file:manifesto-encrypted',
    type: 'SourceFile',
    label: 'המניפסט האחרון',
    attrs: { bucket: 'destination' },
  },
]

let edgeCounter = 0
const e = (
  sourceId: string,
  targetId: string,
  type: EdgeType,
  opts: Partial<Pick<GraphEdge, 'gloss' | 'claimant' | 'status' | 'directional'>> = {},
): GraphEdge => ({
  id: `e${++edgeCounter}`,
  sourceId,
  targetId,
  type,
  gloss: opts.gloss,
  claimant: opts.claimant ?? 'claude',
  status: opts.status ?? 'proposed',
  directional: opts.directional ?? type !== 'echoes',
})

const edges: GraphEdge[] = [
  // mentions (factual; no gloss; status confirmed since they're observable)
  e('section:vns47', 'person:jackie', 'mentions', { status: 'confirmed' }),
  e('section:vns47', 'motif:bird-in-shoebox', 'mentions', { status: 'confirmed' }),
  e('section:vns47', 'source:communist-manifesto', 'mentions', { status: 'confirmed' }),
  e('section:vns47', 'place:field', 'mentions', { status: 'confirmed' }),
  e('section:vns47', 'moment:opened-door', 'mentions', { status: 'confirmed' }),
  e('section:vns47', 'moment:bug-fixed-next', 'mentions', { status: 'confirmed' }),

  e('section:uwe6h', 'person:jackie', 'mentions', { status: 'confirmed' }),
  e('section:uwe6h', 'motif:bird-in-shoebox', 'mentions', { status: 'confirmed' }),
  e('section:uwe6h', 'motif:bird-release', 'mentions', { status: 'confirmed' }),
  e('section:uwe6h', 'place:field', 'mentions', { status: 'confirmed' }),
  e('section:uwe6h', 'source:strangest-stories', 'mentions', { status: 'confirmed' }),

  e('section:k4sm6', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:k4sm6', 'concept:intergenerational-paradox', 'mentions', { status: 'confirmed' }),

  e('section:5t3n9', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:5t3n9', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:5t3n9', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:5t3n9', 'place:tel-aviv', 'mentions', { status: 'confirmed' }),
  e('section:5t3n9', 'concept:recognition', 'mentions', { status: 'confirmed' }),
  e('section:5t3n9', 'concept:moral-math', 'mentions', { status: 'confirmed' }),
  e('section:5t3n9', 'moment:doesnt-add-up', 'mentions', { status: 'confirmed' }),

  e('section:bamju', 'place:bridge', 'mentions', { status: 'confirmed' }),
  e('section:bamju', 'person:swift', 'mentions', { status: 'confirmed' }),
  e('section:bamju', 'person:teacher', 'mentions', { status: 'confirmed' }),
  e('section:bamju', 'moment:i-am-frustrated', 'mentions', { status: 'confirmed' }),

  e('section:ex4uc', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:ex4uc', 'place:tel-aviv', 'mentions', { status: 'confirmed' }),
  e('section:ex4uc', 'place:beach', 'mentions', { status: 'confirmed' }),
  e('section:ex4uc', 'moment:never-asked-baby', 'mentions', { status: 'confirmed' }),

  e('section:l5dr6', 'person:thiel', 'mentions', { status: 'confirmed' }),
  e('section:l5dr6', 'source:thiel-book', 'mentions', { status: 'confirmed' }),

  e('section:v37cy', 'place:eilat', 'mentions', { status: 'confirmed' }),
  e('section:v37cy', 'moment:still-exist', 'mentions', { status: 'confirmed' }),

  e('section:1tye1', 'motif:mosaic', 'mentions', { status: 'confirmed' }),
  e('section:1tye1', 'person:grandfather', 'mentions', { status: 'confirmed' }),
  e('section:1tye1', 'place:lod', 'mentions', { status: 'confirmed' }),
  e('section:1tye1', 'moment:manifesto-mosaic', 'mentions', { status: 'confirmed' }),

  e('section:b9y06', 'person:teacher', 'mentions', { status: 'confirmed' }),

  e('section:34g10', 'person:teacher', 'mentions', { status: 'confirmed' }),
  e('section:34g10', 'concept:protagonist-bug', 'mentions', { status: 'confirmed' }),
  e('section:34g10', 'moment:complain', 'mentions', { status: 'confirmed' }),
  e('section:34g10', 'moment:story-betrayal', 'mentions', { status: 'confirmed' }),

  e('section:c1xqj', 'person:grossman', 'mentions', { status: 'confirmed' }),
  e('section:c1xqj', 'person:rubinstein', 'mentions', { status: 'confirmed' }),
  e('section:c1xqj', 'source:grossman-erasmus', 'mentions', { status: 'confirmed' }),
  e('section:c1xqj', 'source:rubinstein-quote', 'mentions', { status: 'confirmed' }),
  e('section:c1xqj', 'concept:recognition', 'mentions', { status: 'confirmed' }),
  e('section:c1xqj', 'moment:courage-sacrifice', 'mentions', { status: 'confirmed' }),
  e('section:c1xqj', 'moment:last-train', 'mentions', { status: 'confirmed' }),

  e('section:z58pz', 'person:krishnamurti', 'mentions', { status: 'confirmed' }),
  e('section:z58pz', 'source:krishnamurti-quote', 'mentions', { status: 'confirmed' }),
  e('section:z58pz', 'concept:psychological-revolt', 'mentions', { status: 'confirmed' }),
  e('section:z58pz', 'motif:thin-seam', 'mentions', { status: 'confirmed' }),

  e('section:2f3gy', 'motif:button', 'mentions', { status: 'confirmed' }),
  e('section:2f3gy', 'concept:fermi', 'mentions', { status: 'confirmed' }),
  e('section:2f3gy', 'concept:universal-compassion', 'mentions', { status: 'confirmed' }),
  e('section:2f3gy', 'concept:perfect-grace', 'mentions', { status: 'confirmed' }),
  e('section:2f3gy', 'concept:distributed-algorithm', 'mentions', { status: 'confirmed' }),
  e('section:2f3gy', 'moment:would-you-press', 'mentions', { status: 'confirmed' }),
  e('section:2f3gy', 'moment:lift-your-head', 'mentions', { status: 'confirmed' }),
  e('section:2f3gy', 'moment:perfect-grace-line', 'mentions', { status: 'confirmed' }),

  // echoes (semantic; symmetric; with gloss)
  e('section:vns47', 'section:uwe6h', 'echoes', {
    gloss: 'המשך ישיר של אותה סצנה — הילד וג׳קי, מהדלת אל השדה. שני סקשנים שמרכיבים את הפותח של הספר.',
  }),
  e('section:vns47', 'section:z58pz', 'echoes', {
    gloss:
      'זוג כיאסטי בקצוות: שניהם פותחים בסיפור היסטורי (סטלין / קרישנמורטי) ונגזרת ממנו מסקנה ספרותית. כאן מקור־כשל, שם מקור־השראה.',
  }),
  e('section:5t3n9', 'section:c1xqj', 'echoes', {
    gloss:
      '״הכרה״ — שני הסקשנים עוסקים בסתירה המוסרית של הכרה. 5t3n9 מציג את הבעיה (״לא מסתדר מתמטית״); c1xqj חי בה (ציטוט גרוסמן: ״האושר של מי שהקול שלו נשמע וזוכה להכרה״).',
  }),
  e('section:k4sm6', 'section:ex4uc', 'echoes', {
    gloss:
      'זוג אבהות: מבט הוריות לתינוקת + מתמטיקה מוסרית. הפרדוקס הבין־דורי מצד אחד; ״מעולם לא נשאל אם הוא מעוניין״ מצד שני.',
  }),
  e('section:bamju', 'section:b9y06', 'echoes', {
    gloss: '״כתבתי הכל מהסוף״ / ״התחלתי מהסוף, הסוף הכי חשוב לי״. אותה הצהרה מטא־ספרותית בשתי גרסאות.',
  }),
  e('section:bamju', 'section:34g10', 'echoes', {
    gloss: 'אותה דמות ״המורה״; אותה אסתטיקה של תלונה מטא.',
  }),
  e('section:34g10', 'section:b9y06', 'echoes', {
    gloss: 'אותה דמות־דיאלוג ב״המורה״, אותה סצנה (מרפסת, עלי נענע, תה).',
  }),
  e('section:1tye1', 'section:v37cy', 'echoes', {
    gloss:
      'תנועה סימטרית בזמן: 1tye1 מסתכל לאחור (סבא, פוז׳ינב, השואה); v37cy מסתכל קדימה (2222, 3000, 262144). שניהם נטיעת הטקסט מחוץ למשך החיים.',
  }),
  e('motif:bird-release', 'motif:button', 'echoes', {
    gloss:
      'שחרור הציפור הוא הכפתור. ג׳קי משחררת ציפור גוססת מקופסת נעליים → המניפסט מציע לשחרר עולם גוסס. החיבור הארכיטקטוני המרכזי מהפתיחה ליעד.',
  }),

  // compresses_into (directional; sections → encrypted destination)
  e('section:vns47', 'section:2f3gy', 'compresses_into', {
    gloss: '״באג ראשון תוקן. נקסט. יש עוד אלף בדרך״ — ההבטחה למה שיגיע. המוצפן הוא ההתממשות הסופית.',
  }),
  e('section:uwe6h', 'section:2f3gy', 'compresses_into', {
    gloss: 'שחרור הציפור = הכפתור. הסצנה הילדותית הקונקרטית מקדימה את הניסוי המחשבתי.',
  }),
  e('section:ex4uc', 'section:2f3gy', 'compresses_into', {
    gloss: '״מעולם עוד לא נולד הייצור שנשאל מראש אם הוא בכלל מעוניין״ — ההכשר האתי של הכפתור.',
  }),
  e('section:c1xqj', 'section:2f3gy', 'compresses_into', {
    gloss: '״מה שיבדיל בינינו הוא אומץ הלב, מידת ההקרבה״ — הניסוח האנושי של ההכרעה הסופית.',
  }),
  e('section:bamju', 'section:2f3gy', 'compresses_into', {
    gloss: 'קפיצה אישית כצללת הכפתור הקולקטיבי.',
  }),
  e('section:34g10', 'section:2f3gy', 'compresses_into', {
    gloss: '״טקסט לקהל מצומצם״ של ״המורה״ — הפראזה הראשונה של 2f3gy. חוט מבני מילולי.',
  }),

  // in_arc (categorical)
  e('section:k4sm6', 'arc:fatherhood', 'in_arc'),
  e('section:5t3n9', 'arc:fatherhood', 'in_arc'),
  e('section:ex4uc', 'arc:fatherhood', 'in_arc'),
  e('section:1tye1', 'arc:fatherhood', 'in_arc'),
  e('section:v37cy', 'arc:fatherhood', 'in_arc'),
  e('section:c1xqj', 'arc:tikkun-olam', 'in_arc'),
  e('section:2f3gy', 'arc:tikkun-olam', 'in_arc'),
  e('section:34g10', 'arc:tikkun-olam', 'in_arc'),

  // --- Wave 3 edges ---

  // ipv31 ^מחשבית
  e('section:ipv31', 'person:mahshevet', 'mentions', { status: 'confirmed' }),
  e('section:ipv31', 'source:classic-manifestos', 'mentions', { status: 'confirmed' }),
  e('section:ipv31', 'concept:suffering-reduction', 'mentions', { status: 'confirmed' }),
  e('section:ipv31', 'concept:encryption-as-theme', 'mentions', { status: 'confirmed' }),
  e('section:ipv31', 'moment:reduce-suffering', 'mentions', { status: 'confirmed' }),
  e('section:ipv31', 'section:2f3gy', 'compresses_into', {
    gloss:
      '״איאלץ להצפין... אשלח את המפתח כשתהיו מוכנים״ — סיום הדיאלוג עם מחשבית מצביע ישירות על המוצפן. זה הסקשן הכי מטא־מוצהר על קיומו של הצופן.',
  }),
  e('section:ipv31', 'section:l5dr6', 'echoes', {
    gloss:
      'שני זוויות על ״המונופול האידיאולוגי״: l5dr6 דרך ת׳יל (אנושי, כלכלי); ipv31 דרך מחשבית (AI, אלגוריתמי). שתי גרסאות לאותה שאיפה.',
  }),
  e('section:ipv31', 'section:c1xqj', 'echoes', {
    gloss: 'AI כאיום על יוצר הטקסט. c1xqj רץ לפני הרכבת; ipv31 מתאמן את הרכבת לכתוב במקומו. צד נהדם וצד מאמן.',
  }),
  e('section:ipv31', 'arc:tikkun-olam', 'in_arc'),

  // pl881 ^פגישת מיתוג
  e('section:pl881', 'person:designer', 'mentions', { status: 'confirmed' }),
  e('section:pl881', 'concept:half-baked', 'mentions', { status: 'confirmed' }),
  e('section:pl881', 'moment:half-baked', 'mentions', { status: 'confirmed' }),
  e('section:pl881', 'moment:advance-paid', 'mentions', { status: 'confirmed' }),
  e('section:pl881', 'section:2f3gy', 'echoes', {
    gloss:
      '״נולדים חצי אפויים״ — בדיוק הצידוק לכפתור. אם היינו נולדים שלמים היינו נהדמים מהזעקה. ההגנה הקיומית של הילדות היא מקור הסבל.',
  }),
  e('section:pl881', 'section:ex4uc', 'echoes', {
    gloss:
      '״מעולם לא נשאל הייצור אם מעוניין״ (ex4uc) ו״חצי אפויים״ (pl881) — שני ניסוחים של אותה טענת חוסר־הסכמה לקיום.',
  }),
  e('section:pl881', 'arc:tikkun-olam', 'in_arc'),

  // fvrmk ^ביובית
  e('section:fvrmk', 'person:arab-worker', 'mentions', { status: 'confirmed' }),
  e('section:fvrmk', 'motif:kippah', 'mentions', { status: 'confirmed' }),
  e('section:fvrmk', 'concept:assimilation-math', 'mentions', { status: 'confirmed' }),
  e('section:fvrmk', 'moment:define-enemy', 'mentions', { status: 'confirmed' }),
  e('section:fvrmk', 'section:5t3n9', 'echoes', {
    gloss:
      'שניהם משתמשים במתמטיקה כדי לטעון טענה מוסרית/חברתית: 5t3n9 ״לא מסתדר מתמטית״ של הכרה; fvrmk הלוגיקה הפורמלית של היטמעות. המתמטיקה כדרך לחשוף סתירה.',
  }),
  e('section:fvrmk', 'arc:homeland', 'in_arc'),

  // 9smx7 ^ישן בסלון
  e('section:9smx7', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'person:eran', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'person:bar', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'person:oded', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'place:austria', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'motif:jar', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'moment:appreciation', 'mentions', { status: 'confirmed' }),
  e('section:9smx7', 'section:k4sm6', 'echoes', {
    gloss:
      'שני סקשנים שסוגרים ב״הוקרה של הרגע״ הביתי — ״הנה השבוע התחלת ללכת״ (k4sm6) ו״תודה לשנינו על הדבר הזה״ (9smx7). שניהם מסיימים בנגיעה רכה.',
  }),
  e('section:9smx7', 'arc:coupledom', 'in_arc'),
  e('section:9smx7', 'arc:masculinity', 'in_arc'),

  // vq9l7 ^השראה בדרך לעבודה
  e('section:vq9l7', 'place:eilat', 'mentions', { status: 'confirmed' }),
  e('section:vq9l7', 'moment:eilat-finish', 'mentions', { status: 'confirmed' }),
  e('section:vq9l7', 'section:v37cy', 'echoes', {
    gloss:
      'אילת כקצה־הקצה: ב־vq9l7 קצה גיאוגרפי שאליו מגיעים בעוצמת השראה אישית; ב־v37cy קצה־זמני (ליקוי החמה ב־2180). אילת = ״רחוק ככל האפשר״.',
  }),
  e('section:vq9l7', 'arc:coupledom', 'in_arc'),
  e('section:vq9l7', 'arc:masculinity', 'in_arc'),

  // nfzy3 ^2000
  e('section:nfzy3', 'person:ofer', 'mentions', { status: 'confirmed' }),
  e('section:nfzy3', 'person:bar', 'mentions', { status: 'confirmed' }),
  e('section:nfzy3', 'motif:creaky-door', 'mentions', { status: 'confirmed' }),
  e('section:nfzy3', 'place:beach', 'mentions', { status: 'confirmed' }),
  e('section:nfzy3', 'section:v37cy', 'echoes', {
    gloss:
      'יואב מסמן בעצמו: ״להתחיל את הסיפור הזה אחרי ׳איזה דור יתעניין בזה יותר?׳״. שני הסקשנים על קצות הזמן — האחד נופל בעבר (2000), השני בעתיד.',
  }),
  e('section:nfzy3', 'arc:coupledom', 'in_arc'),
  e('section:nfzy3', 'arc:fatherhood', 'in_arc'),

  // הדלת החורקת — מוטיב שמתחיל להתפזר. עוד מופעים יבואו בגלים הבאים.

  // --- Wave 4 edges ---

  // xwgu8 ^דלת חורקת
  e('section:xwgu8', 'motif:creaky-door', 'mentions', { status: 'confirmed' }),
  e('section:xwgu8', 'moment:door-arrived', 'mentions', { status: 'confirmed' }),
  e('section:xwgu8', 'section:2f3gy', 'compresses_into', {
    gloss:
      '״כשהדלת תפסיק לחרוק אני אדע — המניפסט האנושי האחרון הגיע גם לכאן״ — הסקשן מבסס את היעד הסופי כקריטריון מדיד (השכנים, הדלת, המקום הפיזי הקרוב ביותר).',
  }),
  e('section:xwgu8', 'section:nfzy3', 'echoes', {
    gloss:
      'הדלת מופיעה ב־nfzy3 בקצרה (״הנה הדלת החורקת. בא לי שהם יקראו את זה״); כאן היא מקבלת את ההתבססות המלאה שלה כמוטיב.',
  }),
  e('section:xwgu8', 'section:l5dr6', 'echoes', {
    gloss:
      'שתי גישות למניפסט כמונופול: l5dr6 דרך ת׳יל (מסחרי); xwgu8 דרך התפשטות גלובלית של ערכים אוניברסליים. שני צידי המטבע של ״לתפוס את הכל״.',
  }),
  e('section:xwgu8', 'arc:tikkun-olam', 'in_arc'),

  // vkcgj ^תחליף שמות
  e('section:vkcgj', 'person:teacher', 'mentions', { status: 'confirmed' }),
  e('section:vkcgj', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:vkcgj', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:vkcgj', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:vkcgj', 'moment:names-same', 'mentions', { status: 'confirmed' }),
  e('section:vkcgj', 'section:34g10', 'echoes', {
    gloss:
      'אותה דמות־דיאלוג של ״המורה״, אותה אסתטיקה (מרפסת, נענע, תה). שלישייה: 34g10 → b9y06 → vkcgj — שלוש שיחות עם המורה, על הסוף, על הסוד, על השמות.',
  }),
  e('section:vkcgj', 'section:b9y06', 'echoes', {
    gloss: 'אותה סצנה עם המורה. ב־b9y06 הוא מקשיב לקטע על הצפנה; כאן הוא מציע פתרון מעשי (החלף שמות).',
  }),
  e('section:vkcgj', 'arc:tikkun-olam', 'in_arc'),

  // y6rx4 ^אצל בר, עם עודד
  e('section:y6rx4', 'person:oded', 'mentions', { status: 'confirmed' }),
  e('section:y6rx4', 'person:bar', 'mentions', { status: 'confirmed' }),
  e('section:y6rx4', 'place:barcelona', 'mentions', { status: 'confirmed' }),
  e('section:y6rx4', 'place:netherlands', 'mentions', { status: 'confirmed' }),
  e('section:y6rx4', 'section:9smx7', 'echoes', {
    gloss: 'גם בר, גם עודד — אותו צמד חברים מ־^ישן בסלון. הסקשנים מציירים יחד את החיים החברתיים המקיפים את הכתיבה.',
  }),
  e('section:y6rx4', 'arc:homeland', 'in_arc'),

  // odokn ^מעמד הר סיני
  e('section:odokn', 'person:akiva', 'mentions', { status: 'confirmed' }),
  e('section:odokn', 'motif:mosquitoes', 'mentions', { status: 'confirmed' }),
  e('section:odokn', 'moment:descendant-fates', 'mentions', { status: 'confirmed' }),
  e('section:odokn', 'moment:mosquito-apology', 'mentions', { status: 'confirmed' }),
  e('section:odokn', 'section:v37cy', 'echoes', {
    gloss:
      'שני סקשנים פנייה ישירה לדורות עתידיים. v37cy מותח את הזמן קדימה במספרים (2222, 262144); odokn מותח אותו במצבי גורל (אסיר פוליטי? זונה? טבח?).',
  }),
  e('section:odokn', 'section:1tye1', 'echoes', {
    gloss: 'odokn כצד הקדימה של 1tye1 (סבא, פוז׳ינב). 1tye1 שואל ״מי הייתם״; odokn שואל ״מי תהיו״.',
  }),
  e('section:odokn', 'section:2f3gy', 'echoes', {
    gloss:
      '״סליחה על היתושים הלא מוצלחתים שהכחדתי״ — מדידת סבל גם של יתושים = החמלה האוניברסלית של 2f3gy מורחבת לקצה הכי קטן.',
  }),
  e('section:odokn', 'arc:fatherhood', 'in_arc'),

  // kbstp ^הסיכה
  e('section:kbstp', 'motif:pin', 'mentions', { status: 'confirmed' }),
  e('section:kbstp', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:kbstp', 'person:tamar', 'mentions', { status: 'confirmed' }),
  e('section:kbstp', 'section:1tye1', 'echoes', {
    gloss: 'שני סקשני־שורשים: 1tye1 בסבא־הסבא, kbstp בסבתא תמר. שניהם משתמשים בעבודת השורשים של הילדות כעוגן.',
  }),
  e('section:kbstp', 'section:odokn', 'echoes', {
    gloss:
      'שני סקשנים סמוכים בפרק ״צאצאים״. שניהם אורכי־דורות; odokn קדימה, kbstp אחורה (סבתא תמר → דורות עתידיים שיקבלו את הסיכה).',
  }),
  e('section:kbstp', 'arc:fatherhood', 'in_arc'),

  // adkli ^עודר
  e('section:adkli', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:adkli', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:adkli', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:adkli', 'moment:reduce-suffering-purpose', 'mentions', { status: 'confirmed' }),
  e('section:adkli', 'section:2f3gy', 'compresses_into', {
    gloss:
      '״הפחתתי כאב יותר משיצרתי. זו המשמעות של הקיום האנושי בעיני״ — הקלוז של ^עודר מנסח את האקסיומה האתית שעליה הכפתור עומד. הקיום נמדד ביכולת להפחית כאב.',
  }),
  e('section:adkli', 'section:ipv31', 'echoes', {
    gloss:
      'אותה אמירה מילולית מופיעה בשני סקשנים: ^עודר (סצנה ביתית, האב עודר את מחשבותיו) ו־^מחשבית (האב מלמד את ה־AI את המודל). הקרוב לעצמי + הקרוב לעתיד שולחים את אותו ניסוח.',
  }),
  e('section:adkli', 'arc:fatherhood', 'in_arc'),
  e('section:adkli', 'arc:tikkun-olam', 'in_arc'),
  e('section:ipv31', 'moment:reduce-suffering-purpose', 'mentions', { status: 'confirmed' }),

  // rb87n ^דרושה פיזיקאית
  e('section:rb87n', 'person:fizikait', 'mentions', { status: 'confirmed' }),
  e('section:rb87n', 'source:knesset-execution-report', 'mentions', { status: 'confirmed' }),
  e('section:rb87n', 'moment:bring-it-to-read', 'mentions', { status: 'confirmed' }),
  e('section:rb87n', 'section:2f3gy', 'compresses_into', {
    gloss:
      'הזריקה הפיזיקלית של הרעיון לעתיד = ההצפנה. הסקשן מציע מטפורה פיזיקלית לפעולת ההצפנה: זרוק את הטקסט בעוצמה שתחזיק 20 שנה.',
  }),
  e('section:rb87n', 'section:kbstp', 'echoes', {
    gloss: 'שניהם זריקה קדימה בזמן: kbstp לעשרה דורות הלאה (הסיכה), rb87n לעשרים-שלושים שנה (הטקסט שמחכה לרוח התקופה).',
  }),
  e('section:rb87n', 'section:vq9l7', 'echoes', {
    gloss:
      'דמיון מעמיק לשתי דמויות־עזר נשיות: vq9l7 — האישה הדמיונית שמלווה את ההליכה לכתיבה; rb87n — הפיזיקאית שעוזרת בחישוב. דמות־דיאלוג נשית במקום ״המורה״ הגברי.',
  }),
  e('section:rb87n', 'arc:tikkun-olam', 'in_arc'),

  // 0sil4 ^את יודעת
  e('section:0sil4', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:0sil4', 'motif:compass', 'mentions', { status: 'confirmed' }),
  e('section:0sil4', 'moment:tear-compass', 'mentions', { status: 'confirmed' }),
  e('section:0sil4', 'section:9smx7', 'echoes', {
    gloss: 'שני סקשני יערה־בית. 9smx7 — היא בחוץ עם ערן; 0sil4 — היא בבית, מתרגשת מהפגנה. זוג הופכי באותו ציר־זוגיות.',
  }),
  e('section:0sil4', 'arc:coupledom', 'in_arc'),
  e('section:0sil4', 'arc:masculinity', 'in_arc'),

  // --- Wave 5 edges ---

  // g1n8e ^הנאום של אליושה (פרק 22)
  e('section:g1n8e', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:g1n8e', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:g1n8e', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:g1n8e', 'person:aliosha', 'mentions', { status: 'confirmed' }),
  e('section:g1n8e', 'source:karamazov-aliosha', 'mentions', { status: 'confirmed' }),
  e('section:g1n8e', 'motif:jar', 'mentions', { status: 'confirmed' }),
  e('section:g1n8e', 'section:2f3gy', 'compresses_into', {
    gloss:
      '״מי שיאסוף הרבה זיכרונות יפים... יישאר בלבנו רק זיכרון יפה אחד, הרי גם הוא עשוי להושיע אותנו ביום מן הימים״ — הציטוט של אליושה מצדיק את הספר עצמו כזיכרון יפה לדורות. המוצפן הוא הזיכרון האחד.',
  }),
  e('section:g1n8e', 'section:1tye1', 'echoes', {
    gloss: 'שני סקשני־פרידה לבנות: 1tye1 בטביעות־הרגליים של הפסיפס; g1n8e בנאום אליושה. שניהם מנסחים את הספר כירושה.',
  }),
  e('section:g1n8e', 'section:9smx7', 'echoes', {
    gloss: 'הצנצנת חוזרת — ב־9smx7 הילדות כותבות לצנצנת על אוסטריה; ב־g1n8e הצנצנת היא הטקס המשפחתי המרכזי.',
  }),
  e('section:g1n8e', 'section:kbstp', 'echoes', {
    gloss: 'שני סקשנים אחר־עם־הילדות: kbstp עוסק בסיכה (ירושה אחת), g1n8e עוסק בזיכרון (ירושה אחרת).',
  }),
  e('section:g1n8e', 'arc:fatherhood', 'in_arc'),
  e('section:g1n8e', 'arc:coupledom', 'in_arc'),

  // wjpfy ^אי שוויון
  e('section:wjpfy', 'place:yafo', 'mentions', { status: 'confirmed' }),
  e('section:wjpfy', 'section:432rb', 'echoes', {
    gloss:
      'זוג בתוך הנדסה: wjpfy מדגים את ההיררכיה (השוטרים והנהג, מאסק והחנוונאי); 432rb מנסח אותה כעיקרון (״פירמידות שלא ביקשנו לבנות״). שניהם פרקי־מבוא לאותה דחיסה.',
  }),
  e('section:wjpfy', 'section:l5dr6', 'echoes', {
    gloss:
      'שני סקשנים על המונופוליסטים: l5dr6 ת׳יל כאידיאל; wjpfy מאסק כדוגמה למי שלוקח קרדיט לא־שלו. שני צידים של אותה ביקורת.',
  }),
  e('section:wjpfy', 'arc:tikkun-olam', 'in_arc'),

  // 432rb ^פירמידות שלא ביקשנו לבנות
  e('section:432rb', 'motif:pyramids', 'mentions', { status: 'confirmed' }),
  e('section:432rb', 'moment:pyramid-uninvited', 'mentions', { status: 'confirmed' }),
  e('section:432rb', 'moment:terrorist-explanation', 'mentions', { status: 'confirmed' }),
  e('section:432rb', 'section:2f3gy', 'echoes', {
    gloss:
      'החמלה האוניברסלית של 2f3gy חייבת לכלול גם את אלה שאנחנו מכנים טרוריסטים. 432rb עושה את העבודה האתית המקדימה: מסביר שזה תיוג של ״לא הוסבר היטב״.',
  }),
  e('section:432rb', 'section:34g10', 'echoes', {
    gloss:
      'שני סקשני־ביקורת על פירמידות־קשב: 34g10 על פירמידת הסטוריטלינג (פרוטגוניסט/אלמונים); 432rb על פירמידה חברתית. אותה מבנה־ביקורת.',
  }),
  e('section:432rb', 'arc:tikkun-olam', 'in_arc'),

  // vy99k ^עם יערה, בשדה דב
  e('section:vy99k', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'person:gaza-girl', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'person:schopenhauer', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'source:schopenhauer', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'motif:fishing', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'place:sde-dov', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'place:gaza', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'moment:fisherman-math', 'mentions', { status: 'confirmed' }),
  e('section:vy99k', 'section:2f3gy', 'echoes', {
    gloss:
      'אסימטריית הסבל בקנה־מידה של רגע (דייג + דג) ובקנה־מידה של מלחמה (טייס + הורי הילדה). שני המקרים נותנים מספר שלילי — אותה אריתמטיקה של 2f3gy.',
  }),
  e('section:vy99k', 'section:odokn', 'echoes', {
    gloss: 'מוסר היתושים של odokn ומוסר הדגים של vy99k — שני סקשנים שמותחים את החמלה לגבולה הקטן.',
  }),
  e('section:vy99k', 'arc:coupledom', 'in_arc'),
  e('section:vy99k', 'arc:masculinity', 'in_arc'),

  // jdhee ^סיינפלד
  e('section:jdhee', 'person:seinfeld', 'mentions', { status: 'confirmed' }),
  e('section:jdhee', 'motif:pyramids', 'mentions', { status: 'confirmed' }),
  e('section:jdhee', 'moment:break-the-pyramid', 'mentions', { status: 'confirmed' }),
  e('section:jdhee', 'section:34g10', 'echoes', {
    gloss:
      '34g10 מבקר את הסטוריטלינג; jdhee מודה שהמסַפֵּר עצמו הוא הפרוטגוניסט (״אני זה שמלהק את עצמי״). הביקורת + ההודאה בה.',
  }),
  e('section:jdhee', 'section:432rb', 'echoes', {
    gloss:
      'שתי גרסאות של הפירמידה: 432rb כמבנה חברתי כפוי; jdhee כמבנה ספרותי (״אני ארצה שתעריצו אותי״). אותו מוטיב משני מבטים.',
  }),
  e('section:jdhee', 'arc:tikkun-olam', 'in_arc'),
  e('section:jdhee', 'arc:masculinity', 'in_arc'),

  // mp2l0 ^מה מביא אותך אלי?
  e('section:mp2l0', 'person:psychologist', 'mentions', { status: 'confirmed' }),
  e('section:mp2l0', 'moment:fear-disappearing', 'mentions', { status: 'confirmed' }),
  e('section:mp2l0', 'section:5t3n9', 'echoes', {
    gloss:
      'שתי דמויות־ראיון: ב־5t3n9 העיתונאית; ב־mp2l0 הפסיכולוגית. אבל בשני המקרים המסַפֵּר מערער על הסטטוס של הצד השני — שם ע״י ניסוח לא־מסתדר־מתמטית, כאן ע״י החלפת תפקידים.',
  }),
  e('section:mp2l0', 'section:c1xqj', 'echoes', {
    gloss:
      '״אני מפחד להיעלם״ (mp2l0) ו״האושר של מי שהקול שלו נשמע וזוכה להכרה״ (c1xqj/גרוסמן) — שני ניסוחים של אותו פחד מ־anonymity.',
  }),
  e('section:mp2l0', 'arc:tikkun-olam', 'in_arc'),

  // hhsas ^על האש אצל דייב
  e('section:hhsas', 'person:dave', 'mentions', { status: 'confirmed' }),
  e('section:hhsas', 'source:armistice-1918', 'mentions', { status: 'confirmed' }),
  e('section:hhsas', 'motif:meat', 'mentions', { status: 'confirmed' }),
  e('section:hhsas', 'moment:armistice-deaths', 'mentions', { status: 'confirmed' }),
  e('section:hhsas', 'section:vy99k', 'echoes', {
    gloss:
      'שני סקשנים על סבל בעלי חיים בהקשר חברתי: vy99k בים (דיג); hhsas על האש (בשר). דחיסת התודעה הצמחונית מתוך הסביבה הלא־צמחונית.',
  }),
  e('section:hhsas', 'section:2f3gy', 'echoes', {
    gloss: 'המוות של ה־11 בנובמבר כסמל לסבל מיותר — נופל בדיוק על קו ההצדקה של הכפתור: אם הסבל מיותר, למה לחכות?',
  }),
  e('section:hhsas', 'arc:tikkun-olam', 'in_arc'),

  // pzxl2 ^הנדסה קיומית
  e('section:pzxl2', 'person:grandpa-yossi', 'mentions', { status: 'confirmed' }),
  e('section:pzxl2', 'concept:existential-engineering', 'mentions', { status: 'confirmed' }),
  e('section:pzxl2', 'section:adkli', 'echoes', {
    gloss:
      'שני סקשנים שמנסחים את האקסיומה האתית: adkli אישית (״הפחתתי כאב יותר משיצרתי״); pzxl2 כתיאוריית־מקצוע (״הנדסה קיומית״). אותה תזה משתי זוויות.',
  }),
  e('section:pzxl2', 'section:ipv31', 'echoes', {
    gloss:
      'אקסיומת הפחתת הסבל ב־3 ניסוחים: adkli (סצנה ביתית), pzxl2 (תיאוריית־מקצוע), ipv31 (האב מלמד AI). שלשת ההקשרים — ביתי, פילוסופי, טכנולוגי.',
  }),
  e('section:pzxl2', 'section:2f3gy', 'compresses_into', {
    gloss: '״הנדסה קיומית״ כשם למקצוע־על שמטפל בבעיית הסבל = המוצפן כיישום הקיצוני של אותו מקצוע.',
  }),
  e('section:pzxl2', 'arc:fatherhood', 'in_arc'),
  e('section:pzxl2', 'arc:tikkun-olam', 'in_arc'),

  // --- Wave 6 edges ---

  // el0pb ^מציג את עצמי - 1
  e('section:el0pb', 'section:5t3n9', 'echoes', {
    gloss:
      'שני סקשני־הצגה־עצמית: el0pb הלא־רשמי (״אם אזרוק נייר לפח אפספס כי חלש מדי״); 5t3n9 הפורמלי (ראיון, גיל, משפחה). שני כיוונים של אותו תפקיד פתיחה.',
  }),
  e('section:el0pb', 'section:mp2l0', 'echoes', {
    gloss: 'el0pb מציג את הסַפֵּר לנו; mp2l0 מציג אותו לפסיכולוגית. שני קונטקסטים של הצגה־עצמית.',
  }),
  e('section:el0pb', 'section:v37cy', 'echoes', {
    gloss:
      'שניהם מדברים ישירות לדורות עתידיים (״צאצאים יקרים״). el0pb על אבדן ההקשרים הטכנולוגיים; v37cy על אבדן הזיכרון הקולקטיבי.',
  }),
  e('section:el0pb', 'arc:tikkun-olam', 'in_arc'),

  // 5l706 ^למה את עצובה
  e('section:5l706', 'moment:sad-face-superpower', 'mentions', { status: 'confirmed' }),
  e('section:5l706', 'section:adkli', 'echoes', {
    gloss:
      'אקסיומת ״הפחתת הסבל״ של adkli מקבלת כאן את המקור הביוגרפי: הילד שזיהה סבל לפני שהיה לזה שם. ה־5l706 הוא ה־origin story של המוסר.',
  }),
  e('section:5l706', 'section:mp2l0', 'echoes', {
    gloss: '5l706 = ילדות עם זיהוי־עצב כיכולת; mp2l0 = הגוֹר הפסיכולוגית שאליה הילד הזה הופך. שני קצוות של אותו ציר.',
  }),
  e('section:5l706', 'arc:fatherhood', 'in_arc'),

  // lvq6c ^מקלחת
  e('section:lvq6c', 'motif:hot-spring', 'mentions', { status: 'confirmed' }),
  e('section:lvq6c', 'section:z58pz', 'echoes', {
    gloss:
      'שני סקשנים על תובנה ליד מים: z58pz בחוף הים (״התפר הדק בין החול והמים״); lvq6c במקלחת/מעיין חם. מים כ־site of insight.',
  }),

  // kql06 ^מלון האינסוף
  e('section:kql06', 'person:hilbert', 'mentions', { status: 'confirmed' }),
  e('section:kql06', 'source:hilbert-hotel', 'mentions', { status: 'confirmed' }),
  e('section:kql06', 'motif:hilbert-hotel', 'mentions', { status: 'confirmed' }),
  e('section:kql06', 'motif:pyramids', 'mentions', { status: 'confirmed' }),
  e('section:kql06', 'section:432rb', 'echoes', {
    gloss:
      'מלון האינסוף = מטפורה מתמטית לפירמידה. אבל המלון הסופי (״כל אחד מאיתנו זז רק חדר אחד״) מסיים בקורבן בודד שמרוסק על הכביש. אסימטריית הסבל ברמה החברתית.',
  }),
  e('section:kql06', 'section:wjpfy', 'echoes', {
    gloss: 'wjpfy מתעד אי־שוויון בסצנה (השוטרים והנהג); kql06 מנסח את הלוגיקה המתמטית של אותו אי־שוויון.',
  }),
  e('section:kql06', 'arc:tikkun-olam', 'in_arc'),

  // qrd6i ^פירמידה הפוכה
  e('section:qrd6i', 'motif:pyramids', 'mentions', { status: 'confirmed' }),
  e('section:qrd6i', 'section:432rb', 'echoes', {
    gloss:
      'זוג פירמידות: 432rb בפירמידה הרגילה; qrd6i בפירמידה הפוכה (כולם מלכים, משרת אחד). שני קצוות של ניסוי המחשבה.',
  }),
  e('section:qrd6i', 'section:jdhee', 'echoes', {
    gloss:
      '״בראש הפירמידה צריך לעמוד מי שרוצה לשבור אותה״ (jdhee) ↔ ״כוורת הפוכה: זה שמשרת את כולם״ (qrd6i). שתי גרסאות של אותה הצעה.',
  }),
  e('section:qrd6i', 'arc:tikkun-olam', 'in_arc'),

  // z95et ^עם הזמן למדתי
  e('section:z95et', 'person:ofer', 'mentions', { status: 'confirmed' }),
  e('section:z95et', 'moment:slow-heartbeat', 'mentions', { status: 'confirmed' }),
  e('section:z95et', 'section:9smx7', 'echoes', {
    gloss:
      'שתי לילות־לבד: 9smx7 ביעדן לבד עם הילדות (יערה עם ערן); z95et לבד אחרי שהילדות ישנות (יערה עם עופר). שני מבטים מאותה זווית.',
  }),
  e('section:z95et', 'section:5l706', 'echoes', {
    gloss:
      'הילד עם ״פרצוף עצוב״ של 5l706 הוא הבוגר שכותב ב־z95et ״תגיד לי שאתה עצוב בלי לומר שאתה עצוב״. אותו ילד, ארבעים שנה אחר־כך.',
  }),
  e('section:z95et', 'section:bamju', 'echoes', {
    gloss:
      'שני סקשנים בקרבה מסוכנת לחיים: bamju בגשר (קפיצה); z95et במיטה (הילדים ישנים, יערה איתו אחר, ״עוצר חילוף חומרים״). הקפיצה הקפואה.',
  }),
  e('section:z95et', 'arc:coupledom', 'in_arc'),
  e('section:z95et', 'arc:masculinity', 'in_arc'),

  // 6zt53 ^אני כמעט פציפיסט
  e('section:6zt53', 'person:oded', 'mentions', { status: 'confirmed' }),
  e('section:6zt53', 'person:altman', 'mentions', { status: 'confirmed' }),
  e('section:6zt53', 'motif:pyramids', 'mentions', { status: 'confirmed' }),
  e('section:6zt53', 'moment:terrorist-pacifist', 'mentions', { status: 'confirmed' }),
  e('section:6zt53', 'moment:billionaire-no-right', 'mentions', { status: 'confirmed' }),
  e('section:6zt53', 'section:432rb', 'echoes', {
    gloss:
      '״ילכו רק על הבודדים, על ראשי הפירמידה״ — הצעה אופרטיבית למה שתאר 432rb כעיקרון. הקיצוני שב־6zt53 נטוע בניתוח של 432rb.',
  }),
  e('section:6zt53', 'section:l5dr6', 'echoes', {
    gloss: 'l5dr6 מודה במונופוליסטיות של המניפסט; 6zt53 מטיף לחיסול מונופוליסטים. אותה דמות בשני קולות.',
  }),
  e('section:6zt53', 'section:wjpfy', 'echoes', {
    gloss: 'שני סקשנים אנטי־מאסק: wjpfy ביקורתית; 6zt53 אופרטיבית.',
  }),
  e('section:6zt53', 'arc:pacifism', 'in_arc'),
  e('section:6zt53', 'arc:tikkun-olam', 'in_arc'),
  e('section:6zt53', 'arc:homeland', 'in_arc'),

  // vdugi ^לא היה סימן
  e('section:vdugi', 'person:saddam', 'mentions', { status: 'confirmed' }),
  e('section:vdugi', 'motif:saddam-hall', 'mentions', { status: 'confirmed' }),
  e('section:vdugi', 'concept:signal', 'mentions', { status: 'confirmed' }),
  e('section:vdugi', 'moment:saddam-mustache', 'mentions', { status: 'confirmed' }),
  e('section:vdugi', 'moment:no-signal', 'mentions', { status: 'confirmed' }),
  e('section:vdugi', 'section:2f3gy', 'compresses_into', {
    gloss:
      '״המניפסט שואף להיות הסימן הזה״ — vdugi מציב את התפקיד שעליו 2f3gy עונה. בעיית התיאום של האולם של סדאם היא בעיית התיאום של האנושות; הכפתור הוא הסימן הסופי.',
  }),
  e('section:vdugi', 'section:432rb', 'echoes', {
    gloss:
      'הפירמידה של 432rb מתקיימת רק כי ״לא היה סימן״ ב־vdugi. הפחד שמחזיק את הקורבן בכיסא הוא אותו פחד שמחזיק את הפירמידה.',
  }),
  e('section:vdugi', 'section:6zt53', 'echoes', {
    gloss:
      'vdugi מסביר למה כולנו יושבים שקטים; 6zt53 מציע לשבור — ״ילכו רק על ראשי הפירמידה״. הניסוח האנליטי + הצעה לפעולה.',
  }),
  e('section:vdugi', 'arc:tikkun-olam', 'in_arc'),
  e('section:vdugi', 'arc:homeland', 'in_arc'),

  // --- Wave 7 edges ---

  // o3scq ^האורגיה
  e('section:o3scq', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:o3scq', 'section:9smx7', 'echoes', {
    gloss:
      'שלישית סקשני זוגיות פתוחה: 9smx7 (יערה עם ערן, הוא לבד עם הילדות) → z95et (יערה עם עופר, הוא לבד) → o3scq (יערה מולו עם זר, באורגיה). הדרגה — מ״מסביב״ ל״מולו״.',
  }),
  e('section:o3scq', 'section:z95et', 'echoes', {
    gloss: 'הצמד החזק של o3scq + z95et: שניהם מתעדים את אותה זוגיות בסיטואציות הפוכות (במחיצה / כאן יחד).',
  }),
  e('section:o3scq', 'arc:coupledom', 'in_arc'),
  e('section:o3scq', 'arc:masculinity', 'in_arc'),

  // 0vjhk ^תתחילי בעמוד 38
  e('section:0vjhk', 'moment:page-38', 'mentions', { status: 'confirmed' }),
  e('section:0vjhk', 'section:vkcgj', 'echoes', {
    gloss:
      'שני סקשנים על מתן הספר: vkcgj המורה מציע ״תחליף שמות״ כדי לתת את הספר לנשים שבחייו; 0vjhk הספר ניתן לזרה כפעולת חיזור.',
  }),
  e('section:0vjhk', 'section:o5fsv', 'echoes', {
    gloss:
      'שני סקשני חיזור־כושל לאישה זרה: o5fsv (״סליחה, ערב טוב״) הוא הדמיון; 0vjhk הוא הפעולה המוצעת (״אני מציע לך להיות דמות בספר״). כישלון מותנע + הצעה אמיצה.',
  }),
  e('section:0vjhk', 'arc:masculinity', 'in_arc'),
  e('section:0vjhk', 'arc:coupledom', 'in_arc'),

  // o5fsv ^סליחה, ערב טוב
  e('section:o5fsv', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:o5fsv', 'section:0vjhk', 'echoes', {
    gloss: 'ראה את ההסבר ב־0vjhk. שני סקשנים שעוסקים ברגע ה״הזדמנות החמיצה״.',
  }),
  e('section:o5fsv', 'arc:masculinity', 'in_arc'),

  // cmsiv ^לא יכול היה להיות סידור אחר
  e('section:cmsiv', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:cmsiv', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:cmsiv', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:cmsiv', 'section:pl881', 'echoes', {
    gloss:
      'דטרמיניזם רך: cmsiv על השולחן (״לא יכול היה להיות סידור אחר״); pl881 על הילדות (״נולדים חצי אפויים״). מבני־הוויה בלתי־נמנעים.',
  }),
  e('section:cmsiv', 'arc:fatherhood', 'in_arc'),

  // wii5l ^יום השואה
  e('section:wii5l', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:wii5l', 'person:diana-hasson', 'mentions', { status: 'confirmed' }),
  e('section:wii5l', 'place:rodi-italy', 'mentions', { status: 'confirmed' }),
  e('section:wii5l', 'place:auschwitz', 'mentions', { status: 'confirmed' }),
  e('section:wii5l', 'moment:aaron-hasson', 'mentions', { status: 'confirmed' }),
  e('section:wii5l', 'section:1tye1', 'echoes', {
    gloss:
      'שני סקשני־ירושה־של־שואה: 1tye1 על סבא־סבא ופוז׳ינב; wii5l על שמות־קורבן שילד נושא. שני קצוות של אותו ירושה: זיכרון רחוק + זיכרון יומיומי.',
  }),
  e('section:wii5l', 'section:kbstp', 'echoes', {
    gloss:
      'שני סקשני־שמות־בירושה: kbstp הסיכה מועברת לבן הבכור; wii5l שם הקורבן עליו ילד יושב. ירושה שבחרו בה / ירושה שכפויה.',
  }),
  e('section:wii5l', 'arc:fatherhood', 'in_arc'),
  e('section:wii5l', 'arc:homeland', 'in_arc'),

  // fh2np ^יהדות כהשראה
  e('section:fh2np', 'person:nietzsche', 'mentions', { status: 'confirmed' }),
  e('section:fh2np', 'source:nietzsche', 'mentions', { status: 'confirmed' }),
  e('section:fh2np', 'moment:bigger-tribe', 'mentions', { status: 'confirmed' }),
  e('section:fh2np', 'concept:bigger-tribe', 'mentions', { status: 'confirmed' }),
  e('section:fh2np', 'section:mk79t', 'echoes', {
    gloss:
      'זוג סקשני־יהדות בתוך החברה־הישראלית: fh2np דרך השראה היסטורית (לבחור בשבט גדול יותר); mk79t דרך תוקפנות (״המשיח כבר בא והוא אתאיסט״). תזה + אנטיתזה.',
  }),
  e('section:fh2np', 'section:2f3gy', 'echoes', {
    gloss:
      '״השבט הגדול יותר״ הוא הניסוח האידיאולוגי של ״החמלה האוניברסלית״ של 2f3gy. אותה מהות, אבל מנוסחת כקריאה ולא כאקסיומה.',
  }),
  e('section:fh2np', 'arc:homeland', 'in_arc'),

  // mk79t ^אחי, רוצה להניח תפילין?
  e('section:mk79t', 'moment:atheist-messiah', 'mentions', { status: 'confirmed' }),
  e('section:mk79t', 'arc:homeland', 'in_arc'),

  // zza3z ^אם אי פעם תהיו במצב של מלחמה
  e('section:zza3z', 'person:dostoyevsky', 'mentions', { status: 'confirmed' }),
  e('section:zza3z', 'motif:rifle', 'mentions', { status: 'confirmed' }),
  e('section:zza3z', 'moment:rifle-suffering', 'mentions', { status: 'confirmed' }),
  e('section:zza3z', 'concept:pacifist-heroism', 'mentions', { status: 'confirmed' }),
  e('section:zza3z', 'section:2f3gy', 'compresses_into', {
    gloss:
      '״כמה סבל רובה אחד מפחית בממוצע?״ — זה בדיוק ניסוי המחשבה של 2f3gy (״הייתם לוחצים?״) במיקרו. זריקת רובה = זריקת כפתור בכל הצעת־ערך אישית.',
  }),
  e('section:zza3z', 'section:6zt53', 'echoes', {
    gloss:
      '6zt53 מטיף לפציפיזם־טרור (״ילכו רק על ראשי הפירמידה״); zza3z מנסח את הגבורה הפציפיסטית האלטרנטיבית (״ידיים למעלה אצל האויב״). שתי גרסאות מקיצוניות של אותה דחיסה.',
  }),
  e('section:zza3z', 'section:vy99k', 'echoes', {
    gloss: 'אסימטריית הסבל מ־vy99k (הטייס + הילדה מעזה) מקבלת כאן ניסוח שיטתי: כל נשק = יחידת סבל. שיא הציר.',
  }),
  e('section:zza3z', 'arc:pacifism', 'in_arc'),
  e('section:zza3z', 'arc:homeland', 'in_arc'),

  // --- Wave 8 edges ---

  // eu99q ^מחתרת
  e('section:eu99q', 'person:plato', 'mentions', { status: 'confirmed' }),
  e('section:eu99q', 'motif:underground', 'mentions', { status: 'confirmed' }),
  e('section:eu99q', 'moment:lion-and-zebra', 'mentions', { status: 'confirmed' }),
  e('section:eu99q', 'section:0vjhk', 'echoes', {
    gloss: 'eu99q הוא הפנטזיה המוצלחת שמלצר את שאלת 0vjhk (״הייתי רוצה שתהיי דמות״). שניהם על הזרה שכן בוחרת.',
  }),
  e('section:eu99q', 'section:o3scq', 'echoes', {
    gloss: 'בגרסת eu99q הזרה היא מהפכנית; ב־o3scq היא בתוך אורגיה. שני קונטקסטים לאותה דמות.',
  }),
  e('section:eu99q', 'section:6zt53', 'echoes', {
    gloss:
      '״המחתרת״ כסמל לקבוצת התנגדות נגד ההיררכיה. 6zt53 פציפיזם־טרור; eu99q המחתרת הזוגית. שתי גרסאות של פעולה־תת־קרקעית.',
  }),
  e('section:eu99q', 'arc:masculinity', 'in_arc'),
  e('section:eu99q', 'arc:coupledom', 'in_arc'),

  // f4cwz ^פרס נובל
  e('section:f4cwz', 'person:dylan', 'mentions', { status: 'confirmed' }),
  e('section:f4cwz', 'moment:king-not-prophet', 'mentions', { status: 'confirmed' }),
  e('section:f4cwz', 'concept:king-vs-prophet', 'mentions', { status: 'confirmed' }),
  e('section:f4cwz', 'section:l5dr6', 'echoes', {
    gloss:
      'l5dr6 שואף למונופול אידיאולוגי (מלך); f4cwz מסביר מדוע מלכים לא יכולים להיות נביאים. תזה ואנטיתזה באותו פרק־מטא.',
  }),
  e('section:f4cwz', 'section:jdhee', 'echoes', {
    gloss: 'שני סקשני־סופר־כפרוטגוניסט: jdhee ״אני זה שמלהק את עצמי״; f4cwz ״אתה זה הם״. שתי הודאות באותה בעיה.',
  }),
  e('section:f4cwz', 'arc:tikkun-olam', 'in_arc'),

  // 0k1m6 ^תפרוס לי את זה
  e('section:0k1m6', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:0k1m6', 'section:0vjhk', 'echoes', {
    gloss:
      'הציר התשוקה־הכרה: 0k1m6 הוא הרגע שכבר אחרי ההצלחה (״לא עובר שני מעברי חצייה בלי שגברים מפרגנים״); 0vjhk הוא הצעת הספר כניסיון להפוך הכרה ליחסים.',
  }),
  e('section:0k1m6', 'section:5t3n9', 'echoes', {
    gloss: 'שניהם על הצלחת הטקסט. 5t3n9 ההצלחה ״לא מסתדרת מתמטית״; 0k1m6 מציע פתרון: ״תפרוס לי את זה״.',
  }),
  e('section:0k1m6', 'arc:masculinity', 'in_arc'),

  // j0p47 ^אבא
  e('section:j0p47', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:j0p47', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:j0p47', 'moment:father-walks-away', 'mentions', { status: 'confirmed' }),
  e('section:j0p47', 'section:5l706', 'echoes', {
    gloss:
      '5l706 הילד שמזהה עצב במבוגרים; j0p47 הילד שמזהה מבוגר כילד (״העיניים שהביטו בי היו עיניים של ילד״). אותו טבע, ארבעים שנה אחר־כך — והפעם הוא מתרחק.',
  }),
  e('section:j0p47', 'section:wii5l', 'echoes', {
    gloss: 'שני סקשנים על שואה־בלי־להגיד־שואה: wii5l שם של קורבן על נר; j0p47 ניצול שואה שקורא ״אבא?״. הזיהוי בכפיפה.',
  }),
  e('section:j0p47', 'arc:fatherhood', 'in_arc'),

  // qjxw2 ^הצפה
  e('section:qjxw2', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'person:bruno-amadio', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'motif:crying-boy', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'motif:flood', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'moment:please-disconnect', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'moment:mufasa-quote', 'mentions', { status: 'confirmed' }),
  e('section:qjxw2', 'section:bamju', 'echoes', {
    gloss:
      'bamju בגשר (קפיצה אישית כאופציה); qjxw2 ״ביקשת להתנתק... כל יום בחמשת החודשים האחרונים״. השוואה מבעיתה: הקפיצה כבר מתקיימת כבקשה יומית.',
  }),
  e('section:qjxw2', 'section:z95et', 'echoes', {
    gloss:
      'z95et ״אני מאט את קצב פעימות הלב, עוצר את חילוף החומרים״; qjxw2 ״ביקשת להתנתק״. שני סקשנים של ניתוק־מצמיתה.',
  }),
  e('section:qjxw2', 'section:5l706', 'echoes', {
    gloss: '5l706 הילד שזיהה את עצב אמא; qjxw2 הילדה (נלי) שמזהה את עצב מופאסה. אותה רגישות, ירושה דורית.',
  }),
  e('section:qjxw2', 'arc:fatherhood', 'in_arc'),
  e('section:qjxw2', 'arc:coupledom', 'in_arc'),

  // 6beuw ^יהדות - נאום תוכחה
  e('section:6beuw', 'section:fh2np', 'echoes', {
    gloss:
      'שלישית סקשני־יהדות בתוך החברה־הישראלית: fh2np (השראה היסטורית, ניטשה); mk79t (״משיח אתאיסט״); 6beuw (תוכחה ישירה לחרדים). שלוש זוויות.',
  }),
  e('section:6beuw', 'section:mk79t', 'echoes', {
    gloss: 'שניהם דיאלוגים־פולמוסיים עם דתיים. 6beuw אנליטי (״אתם לגאסי קוד״); mk79t פנייה ישירה (״תניח לי מהתפילין״).',
  }),
  e('section:6beuw', 'arc:homeland', 'in_arc'),

  // 7pxpc ^ממריא לאוסטרליה
  e('section:7pxpc', 'motif:kippah', 'mentions', { status: 'confirmed' }),
  e('section:7pxpc', 'moment:passport-israeli', 'mentions', { status: 'confirmed' }),
  e('section:7pxpc', 'section:y6rx4', 'echoes', {
    gloss:
      '7pxpc הרכבת לטיסה; y6rx4 חוזר מטיסה בברצלונה. שני קצוות של אותה תנועה — לעוף מהארץ ולחזור — ובשניהם זיהוי הזרות־ביתית.',
  }),
  e('section:7pxpc', 'section:fvrmk', 'echoes', {
    gloss: 'fvrmk העובד עם הכיפה (אסימילציה); 7pxpc החייל עם הכיפה (קבוצת־כוח). אותו סממן, שני קוטבי משמעות.',
  }),
  e('section:7pxpc', 'arc:homeland', 'in_arc'),

  // 26n6m ^מונולוג
  e('section:26n6m', 'person:shlomi', 'mentions', { status: 'confirmed' }),
  e('section:26n6m', 'concept:monologue-vs-dialogue', 'mentions', { status: 'confirmed' }),
  e('section:26n6m', 'moment:wars-from-dialogue', 'mentions', { status: 'confirmed' }),
  e('section:26n6m', 'section:34g10', 'echoes', {
    gloss:
      '34g10 ביקורת על הסטוריטלינג (פרוטגוניסט/אלמונים); 26n6m ביקורת על הדיאלוג (״מלחמות שהתחילו מהצורך שלנו בדיאלוגים״). שני ערעורים על מבני־סיפור.',
  }),
  e('section:26n6m', 'section:bamju', 'echoes', {
    gloss:
      '״מלחמות מהצורך בדיאלוגים״ ב־26n6m מתחבר ל־״אני מתוסכל מכם״ של bamju — בשני המקרים הקול הסולן מואשם בכך שהוא לא די דרמטי.',
  }),
  e('section:26n6m', 'arc:tikkun-olam', 'in_arc'),

  // --- Wave 9 edges ---

  // shg1c ^פצוע
  e('section:shg1c', 'motif:disappearance', 'mentions', { status: 'confirmed' }),
  e('section:shg1c', 'moment:want-to-disappear', 'mentions', { status: 'confirmed' }),
  e('section:shg1c', 'section:bamju', 'echoes', {
    gloss:
      'מקבץ האובדנות: bamju (קפיצה כאופציה), qjxw2 (חמישה חודשי בקשה־להתנתק), z95et (עצירת חילוף החומרים), shg1c (תכנון יציאה עם 150 אלף דולר). ארבעה ניסוחים של אותה נטייה.',
  }),
  e('section:shg1c', 'section:qjxw2', 'echoes', {
    gloss: 'ראה bamju gloss.',
  }),
  e('section:shg1c', 'section:z95et', 'echoes', {
    gloss: 'ראה bamju gloss.',
  }),
  e('section:shg1c', 'arc:masculinity', 'in_arc'),

  // fjggr ^יש לך פייסל?
  e('section:fjggr', 'moment:turtle-faisal', 'mentions', { status: 'confirmed' }),
  e('section:fjggr', 'section:7pxpc', 'echoes', {
    gloss:
      'שניהם משתמשים בשאלת ״איפה היית כשרבין/התאומים״ כעוגן דורי. 7pxpc במציאות (פוסט־7/10); fjggr בדמיון של דיאלוג־צב.',
  }),

  // 6n3p1 ^זיקנה
  e('section:6n3p1', 'moment:tibetan-monks-snow', 'mentions', { status: 'confirmed' }),
  e('section:6n3p1', 'section:lvq6c', 'echoes', {
    gloss: 'מקלחת כמרחב־תובנה (lvq6c) ↔ הזיקנה כמרחב־הכרה־בקץ (6n3p1). שני חדרים סגורים שבהם משהו נחשף.',
  }),
  e('section:6n3p1', 'section:2f3gy', 'echoes', {
    gloss: 'הנזירים הטיבטים שיוצאים למות בשלג = גרסה ארכאית של הכפתור. הקץ המבוקש מתוך חמלה־עצמית.',
  }),
  e('section:6n3p1', 'section:6qpg1', 'echoes', {
    gloss:
      'מעגל אבהות־וזקנה: 6qpg1 ״הבאתי אתכן כי הייתי חלש״ (פחד מבדידות בזיקנה) → 6n3p1 הזיקנה עצמה. המוטיב המתואר ב־6qpg1 מתממש ב־6n3p1.',
  }),
  e('section:6n3p1', 'arc:masculinity', 'in_arc'),
  e('section:6n3p1', 'arc:coupledom', 'in_arc'),

  // 3ycdx ^רוצה כיף
  e('section:3ycdx', 'section:0vjhk', 'echoes', {
    gloss: 'שני סקשנים על אסימטריה מינית גברית: 0vjhk הצעת הספר כאופציה; 3ycdx ניתוח אקדמי של מדוע הצעה זו נדרשת.',
  }),
  e('section:3ycdx', 'arc:masculinity', 'in_arc'),
  e('section:3ycdx', 'arc:coupledom', 'in_arc'),

  // 6qpg1 ^הייתי חלש
  e('section:6qpg1', 'concept:born-from-weakness', 'mentions', { status: 'confirmed' }),
  e('section:6qpg1', 'moment:born-weak', 'mentions', { status: 'confirmed' }),
  e('section:6qpg1', 'section:k4sm6', 'echoes', {
    gloss:
      'k4sm6 ״הפרדוקס הבין־דורי״ של הכאב; 6qpg1 מודה שההולדה עצמה הייתה ביטוי לחולשה. שתי הודאות שתופסות את אותו פינה: היכן ההורות פוגעת בילד.',
  }),
  e('section:6qpg1', 'section:ex4uc', 'echoes', {
    gloss:
      '״מעולם לא נולד הייצור שנשאל אם הוא מעוניין״ (ex4uc) ו־״הבאתי אתכן כי הייתי חלש״ (6qpg1) — שתי גרסאות של אותה הודאה. ex4uc האוניברסלית; 6qpg1 האישית.',
  }),
  e('section:6qpg1', 'arc:fatherhood', 'in_arc'),
  e('section:6qpg1', 'arc:masculinity', 'in_arc'),

  // igclx ^הדבר הכי עצוב בעולם
  e('section:igclx', 'motif:old-photographs', 'mentions', { status: 'confirmed' }),
  e('section:igclx', 'section:wii5l', 'echoes', {
    gloss:
      'igclx על תמונות ילדים מלפני 100 שנה (״שום דבר כבר לא אפשרי״); wii5l שם של קורבן שואה. שני סקשנים על מה שלא יישא יותר אפשרות.',
  }),
  e('section:igclx', 'section:v37cy', 'echoes', {
    gloss: 'igclx מסתכל לאחור על דורות אבודים; v37cy לקראת דורות אבודים פוטנציאלית. שני קצוות של אותה תוגה.',
  }),
  e('section:igclx', 'arc:fatherhood', 'in_arc'),

  // hiw9o ^חלום: טיול שנתי
  e('section:hiw9o', 'section:5l706', 'echoes', {
    gloss:
      'שני סקשני־ילדות־מבודדת: 5l706 הילד עם פרצוף עצוב; hiw9o חלום הילד הלבדי במגרש שלישי בין בנים לבנות. אותה דמות, שני זמנים.',
  }),
  e('section:hiw9o', 'section:z95et', 'echoes', {
    gloss: 'הילד מ־hiw9o הוא הבוגר ב־z95et שעדיין לבדו (״אני לא יכול לנדנד יותר״).',
  }),
  e('section:hiw9o', 'arc:masculinity', 'in_arc'),

  // dzrho ^אני יודעת מי אתה
  e('section:dzrho', 'place:sydney', 'mentions', { status: 'confirmed' }),
  e('section:dzrho', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:dzrho', 'moment:i-know-who-you-are', 'mentions', { status: 'confirmed' }),
  e('section:dzrho', 'section:0k1m6', 'echoes', {
    gloss:
      'משולש ההכרה־הפיזית: 0k1m6 ההכרה בתל אביב (״גברים מפרגנים, נשים מדברות איתי״); dzrho ההכרה בסידני (אישה שזיהתה דרך הראיון); 0vjhk הניסיון להפוך הכרה ליחסים. הזרות התרבותית של סידני מבליטה את התופעה.',
  }),
  e('section:dzrho', 'section:7pxpc', 'echoes', {
    gloss: 'שני סקשנים באותו טיול: 7pxpc הדרך לאוסטרליה; dzrho הגעה לסידני. דיפטיכון.',
  }),
  e('section:dzrho', 'section:eu99q', 'echoes', {
    gloss: 'שני סקשנים של ״אישה ניגשת״: eu99q במחתרת הדמיונית; dzrho בסידני המציאותית. הפנטזיה וההגשמה.',
  }),
  e('section:dzrho', 'arc:masculinity', 'in_arc'),

  // --- Wave 10 edges ---

  // cnx02 ^נרדם על הבטן
  e('section:cnx02', 'moment:planet-under-belly', 'mentions', { status: 'confirmed' }),
  e('section:cnx02', 'section:lvq6c', 'echoes', {
    gloss:
      'שני סקשני־גוף־בסיטואציית־בידוד: lvq6c במקלחת (תובנה דרך מים חמים); cnx02 על הבטן (חיבוק כדור הארץ). הגוף כמרחב חיפוש־אינטימי.',
  }),
  e('section:cnx02', 'section:z95et', 'echoes', {
    gloss:
      '״בזמן האחרון אני לא מצליח להירדם״ של cnx02 ↔ ״אני מאט את קצב פעימות הלב, ממתין לאביב״ של z95et. נדודי שינה כביטוי לדיכאון.',
  }),
  e('section:cnx02', 'arc:masculinity', 'in_arc'),

  // 1z89o ^דלקת חניכיים
  e('section:1z89o', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:1z89o', 'person:eran', 'mentions', { status: 'confirmed' }),
  e('section:1z89o', 'section:9smx7', 'echoes', {
    gloss:
      'ערן חוזר. ב־9smx7 הוא הצד השני של יערה; ב־1z89o הוא חבר בקבוצת הצ׳ט שיערה מעדכנת בריב. אותו אדם, שני תפקידים.',
  }),
  e('section:1z89o', 'arc:coupledom', 'in_arc'),
  e('section:1z89o', 'arc:masculinity', 'in_arc'),

  // p93pj ^אחרי שגברת מס׳ 31 זרקה אותי
  e('section:p93pj', 'person:michal-tania', 'mentions', { status: 'confirmed' }),
  e('section:p93pj', 'section:3ycdx', 'echoes', {
    gloss:
      'שני סקשנים תיאורטיים על מגדר: 3ycdx ניתוח שחרור מיני וזנות; p93pj טענה ש״יחסים פתוחים = מטריארכיה מול מראה״. שניהם מבקרים את ה־״גרסה מודרנית״.',
  }),
  e('section:p93pj', 'section:0vjhk', 'echoes', {
    gloss: 'p93pj אחרי דחייה מספר 31; 0vjhk הצעה אמיצה לזרה. שני קצוות של אותו ניסיון.',
  }),
  e('section:p93pj', 'arc:coupledom', 'in_arc'),
  e('section:p93pj', 'arc:masculinity', 'in_arc'),

  // 8t1ae ^עם נלי בחוף נחשולים
  e('section:8t1ae', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:8t1ae', 'place:gaza', 'mentions', { status: 'confirmed' }),
  e('section:8t1ae', 'moment:nelly-fire-die', 'mentions', { status: 'confirmed' }),
  e('section:8t1ae', 'section:vy99k', 'echoes', {
    gloss:
      'שני סקשני־חוף־ועזה־בוערת: vy99k עם יערה (דייגים, דגים, ילדה בת 5); 8t1ae עם נלי (״כיף שאתה מתפורר״). בשני המקרים שיחה אישית רגישה על רקע מלחמה רחוקה.',
  }),
  e('section:8t1ae', 'section:wii5l', 'echoes', {
    gloss:
      'שני סקשני־נלי־עם־מוות: wii5l הנר ליום השואה; 8t1ae הילדה שאומרת ״הייתי רוצה למות משריפה״. הילדה־שמזהה־סבל ממשיכה את הילד מ־5l706.',
  }),
  e('section:8t1ae', 'arc:fatherhood', 'in_arc'),

  // hftnk ^בלוני הליום
  e('section:hftnk', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:hftnk', 'section:igclx', 'echoes', {
    gloss:
      'שני סקשני־רגעי־יופי־ארעיים: igclx על תמונות ילדים מלפני 100 שנה (״שום דבר לא אפשרי לגביהם״); hftnk על בלוני הליום בריחוף (״האם רגעים יפים מראש נדירים?״). שני קצוות של אותה תהייה.',
  }),
  e('section:hftnk', 'arc:fatherhood', 'in_arc'),

  // th813 ^חלקו לפיסקאות
  e('section:th813', 'person:hegel', 'mentions', { status: 'confirmed' }),
  e('section:th813', 'source:hegel', 'mentions', { status: 'confirmed' }),
  e('section:th813', 'section:fvrmk', 'echoes', {
    gloss:
      'th813 ו־fvrmk צמודים בקובץ ובנושא: שניהם על מי שלא הצליחו להסביר את עצמם — fvrmk העובד עם הכיפה (״עכשיו תגדירו אויב״); th813 על אנשים שדחפו לאן שדחפו (תימנים מאתלית). הוקרה למוסקים מתחת לרדאר.',
  }),
  e('section:th813', 'section:l5dr6', 'echoes', {
    gloss:
      '״כתבתם מניפסט? חלקו אותו לפיסקאות״ ב־th813 = מטא־הוראה. ב־l5dr6 מטא־הוראה אחרת: ״תהיו מונופול״. שתי הוראות־כתיבה מטא.',
  }),
  e('section:th813', 'arc:homeland', 'in_arc'),

  // c1s75 ^נושאים לשיחה
  e('section:c1s75', 'section:y6rx4', 'echoes', {
    gloss:
      'c1s75 מנסח: ״ספורט, בשר, ביטחון״; y6rx4 הוא דוגמת־שיחה־אמיתית עם עודד שנוגעת בדיוק בנושאים האלה. תיאוריה + ממצא.',
  }),
  e('section:c1s75', 'arc:masculinity', 'in_arc'),

  // 8bnsj ^ארוחת צהריים במשרד - 2
  e('section:8bnsj', 'person:shlomi', 'mentions', { status: 'confirmed' }),
  e('section:8bnsj', 'moment:speaking-to-vampires', 'mentions', { status: 'confirmed' }),
  e('section:8bnsj', 'section:y6rx4', 'echoes', {
    gloss:
      'שני סקשני־שיחה־חברתית־על־ישראל: y6rx4 בארץ עם בר ועודד (ביקורת); 8bnsj במשרד עם הצוות (ניסיון לדבר). בשני המקרים: שיחה על המדינה שמסתיימת בריחוק.',
  }),
  e('section:8bnsj', 'section:6beuw', 'echoes', {
    gloss:
      '״אני כבר מדבר אל ערפדים״ ב־8bnsj ↔ ״כמה זמן לוקח לכם להוציא פסק הלכה בנוגע למובן מאליו, אתם לגאסי קוד״ ב־6beuw. שתי תחושות־הזרה: חרדים, ערפדים.',
  }),
  e('section:8bnsj', 'arc:homeland', 'in_arc'),

  // --- Wave 11 edges ---

  // obmcs ^פעם כתבתי קצר
  e('section:obmcs', 'moment:short-life-spans', 'mentions', { status: 'confirmed' }),
  e('section:obmcs', 'section:shg1c', 'echoes', {
    gloss:
      'obmcs ״אני לא אדם של שנים רבות״; shg1c ״לפעמים אני פשוט רוצה להיעלם״. הראשון תיאורי־פסיבי, השני אקטיבי. שני קצוות של אותה תפיסת־עצמי.',
  }),
  e('section:obmcs', 'section:26n6m', 'echoes', {
    gloss: 'שני סקשני־מטא־פורמליים: 26n6m על מונולוג מול דיאלוג; obmcs על משפט קצר מול ארוך. הצורה כאתיקה.',
  }),

  // 3rcmm ^יער בן שמן
  e('section:3rcmm', 'section:6beuw', 'echoes', {
    gloss: '6beuw תקפנות אנליטית לחרדים; 3rcmm מפגש אנושי קסום עם חרדל״שית בבריחה. שתי גישות הופכיות לאותה הזרה.',
  }),
  e('section:3rcmm', 'section:0vjhk', 'echoes', {
    gloss: 'מקבץ ההתקרבות הלא־ראשונית: 0vjhk הצעת הספר; 3rcmm ריקוד עם חרדל״שית. שניים מקבץ ה־״אישה ניגשת״.',
  }),

  // k90lu ^דני ונלי רבות
  e('section:k90lu', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:k90lu', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:k90lu', 'section:k4sm6', 'echoes', {
    gloss: 'שני סקשני־הורות־פרגמטית: k4sm6 ניסוח פרדוקס הכאב הבין־דורי; k90lu מימוש פתרון פרגמטי בריב יומיומי.',
  }),
  e('section:k90lu', 'arc:fatherhood', 'in_arc'),

  // sae6e ^הבטחה
  e('section:sae6e', 'concept:promised-death', 'mentions', { status: 'confirmed' }),
  e('section:sae6e', 'moment:promise-to-release', 'mentions', { status: 'confirmed' }),
  e('section:sae6e', 'section:2f3gy', 'compresses_into', {
    gloss:
      'sae6e הוא הכפתור באישי: ״אם אגיע לרגע שאחליט לסיים, אשאל אם אתן משחררות אותי״. 2f3gy הוא אותה בקשה ברמה האנושית — האם אנחנו משחררים את העולם. מרכז־עוצמה חדש בגרף.',
  }),
  e('section:sae6e', 'section:bamju', 'echoes', {
    gloss:
      'מקבץ ההיעלמות מקבל כאן ניסוח חוקתי: bamju הקפיצה, shg1c התכנון, qjxw2 הבקשה היומית — sae6e ההבטחה לתנאי־שחרור. ארבע גרסאות, מחזיקות יחד את הציר.',
  }),
  e('section:sae6e', 'section:shg1c', 'echoes', {
    gloss: 'ראה bamju.',
  }),
  e('section:sae6e', 'arc:fatherhood', 'in_arc'),

  // 9nez9 ^גם אני הייתי נוקם
  e('section:9nez9', 'moment:would-take-revenge', 'mentions', { status: 'confirmed' }),
  e('section:9nez9', 'section:6zt53', 'echoes', {
    gloss:
      '6zt53 פציפיזם־טרור (״רק על ראשי הפירמידה״); 9nez9 הזדהות עם הביביסטים כנוקמים. אותה לוגיקה: רגישות־יתר → רצון לנקמה ממוקדת. ההבדל: 6zt53 מהשמאל הרגיש; 9nez9 מנסה להבין את הימין הרגיש.',
  }),
  e('section:9nez9', 'section:432rb', 'echoes', {
    gloss: '432rb ניתוח הפירמידה; 9nez9 איך מי שתחת הפירמידה (מזרחים) מגיב. השלמה.',
  }),
  e('section:9nez9', 'section:8bnsj', 'echoes', {
    gloss:
      '8bnsj ״אני מדבר אל ערפדים״ (זרות מהמרכז); 9nez9 ״יש בינינו הרבה יותר משותף מאשר בינינו למרכז״ (קרבה עם הקצה השני). שני קצות הוויתור על המרכז.',
  }),
  e('section:9nez9', 'arc:homeland', 'in_arc'),

  // xfwea ^העם הכי מטומטם בהיסטוריה
  e('section:xfwea', 'moment:stupidest-nation', 'mentions', { status: 'confirmed' }),
  e('section:xfwea', 'section:8bnsj', 'echoes', {
    gloss:
      'שני סקשני־רוגז־פוסט־7/10 כלפי החברה הישראלית: 8bnsj ״אני מדבר אל ערפדים״ (פסיבי); xfwea ״אנחנו העם הכי מטומטם בהיסטוריה״ (אקטיבי). שני קולות של אותה הזרה.',
  }),
  e('section:xfwea', 'section:9nez9', 'echoes', {
    gloss: 'מטא־צד: 9nez9 מבין את הימין הרגיש; xfwea מתרגז על הרוב המנותק. דיאלוג פנימי על מי באמת לכעוס.',
  }),
  e('section:xfwea', 'arc:homeland', 'in_arc'),

  // pg4q6 ^חוזר מבר, ריח של זפת
  e('section:pg4q6', 'moment:born-won-died', 'mentions', { status: 'confirmed' }),
  e('section:pg4q6', 'section:ex4uc', 'echoes', {
    gloss:
      'שני סקשני־עובדים־בלילה: ex4uc השוטף את חדר המדרגות (״לא מים ולא עוגיה״); pg4q6 הסוללים אספלט (״הם חיים פחות זמן ממני״). אסימטריית קיום שמתרחשת ברגעי־בית קטנים.',
  }),
  e('section:pg4q6', 'section:wjpfy', 'echoes', {
    gloss: 'שניהם תיעוד אי־שוויון בפעולה: wjpfy השוטרים והנהג; pg4q6 הסוללים אספלט. מציאות־רחוב בלי תיאוריה.',
  }),
  e('section:pg4q6', 'arc:tikkun-olam', 'in_arc'),

  // 9ij28 ^מחשבות על כתיבה
  e('section:9ij28', 'section:vkcgj', 'echoes', {
    gloss: 'שני סקשני־מטא־כתיבה: vkcgj על שמות (החלפת זהויות); 9ij28 על קצב (״תקראו את זה לאט״). שתי הוראות לקוראים.',
  }),
  e('section:9ij28', 'section:b9y06', 'echoes', {
    gloss: 'שני סקשני־ראיון־על־הספר: b9y06 ״התחלתי מהסוף״; 9ij28 ״התחלתי לפני 25 שנה״. שני זמני־כתיבה — בקצה ובמשך.',
  }),
  e('section:9ij28', 'arc:tikkun-olam', 'in_arc'),

  // --- Wave 12 edges ---

  // f0gcr ^רינה
  e('section:f0gcr', 'person:rina', 'mentions', { status: 'confirmed' }),
  e('section:f0gcr', 'person:oded', 'mentions', { status: 'confirmed' }),
  e('section:f0gcr', 'moment:half-married', 'mentions', { status: 'confirmed' }),
  e('section:f0gcr', 'section:6n3p1', 'echoes', {
    gloss:
      'שני סקשני־גבר־בגיל־ביניים: 6n3p1 הזיקנה הפיזית; f0gcr ״לא לגמרי נשוי, אבל גם לא לגמרי פנוי״. שני קטבים של אותו מצב.',
  }),
  e('section:f0gcr', 'section:3rcmm', 'echoes', {
    gloss:
      'זוג מפגשים עם נשים בתוך־דת: 3rcmm חרדל״שית בריקוד; f0gcr רינה הדתית שמחפשת מסורתי. שני קצוות של אותו עניין.',
  }),
  e('section:f0gcr', 'arc:masculinity', 'in_arc'),
  e('section:f0gcr', 'arc:coupledom', 'in_arc'),

  // rlykw ^אנשים שאני אולי מכיר
  e('section:rlykw', 'person:shlomi', 'mentions', { status: 'confirmed' }),
  e('section:rlykw', 'section:26n6m', 'echoes', {
    gloss: 'שתי שיחות מטא־כתיבה עם שלומי: 26n6m על מונולוג/דיאלוג; rlykw על קונקרטי/כללי. שלומי כפרשן־פנימי.',
  }),
  e('section:rlykw', 'section:9ij28', 'echoes', {
    gloss: 'שני סקשני־אתיקה־של־כתיבה: 9ij28 על הקצב; rlykw על הקונקרטיות. כללי כתיבה שמתנגשים עם הימנעות מפגיעה.',
  }),
  e('section:rlykw', 'arc:tikkun-olam', 'in_arc'),

  // 2m1t5 ^השייט באילת
  e('section:2m1t5', 'place:eilat', 'mentions', { status: 'confirmed' }),
  e('section:2m1t5', 'section:fvrmk', 'echoes', {
    gloss:
      'שני סקשני־סממנים־עדתיים: fvrmk העובד עם הכיפה (אסימילציה הפוכה); 2m1t5 שייט עם קברניט אשכנזי + מוזיקה מזרחית. שני ניסיונות־הכלה־שלא־מסתדרים.',
  }),
  e('section:2m1t5', 'section:7pxpc', 'echoes', {
    gloss: 'שני סקשני־מבט־על־הזהויות־הישראליות: 7pxpc הרכבת לטיסה; 2m1t5 השייט. בשניהם המספר רואה את המגוון מבחוץ.',
  }),
  e('section:2m1t5', 'arc:homeland', 'in_arc'),

  // ciuso ^גנדהי סטייל
  e('section:ciuso', 'person:gandhi', 'mentions', { status: 'confirmed' }),
  e('section:ciuso', 'moment:olive-branches-up', 'mentions', { status: 'confirmed' }),
  e('section:ciuso', 'section:zza3z', 'echoes', {
    gloss:
      'zza3z מגדיר את הגבורה הפציפיסטית (״ידיים למעלה אצל האויב״); ciuso מתאר את הציפייה לה (מצד הפלסטינים). אותה תפיסה משני צדדי המתרס.',
  }),
  e('section:ciuso', 'section:6zt53', 'echoes', {
    gloss:
      'שני סקשני־פציפיזם־רדיקלי: 6zt53 אופציה אלימה (״ראשי הפירמידה״); ciuso אופציה לא־אלימה (״ענפי זית״). שני קצוות של אותו דחף.',
  }),
  e('section:ciuso', 'arc:pacifism', 'in_arc'),
  e('section:ciuso', 'arc:homeland', 'in_arc'),

  // p95te ^שישי בבוקר בעיר
  e('section:p95te', 'moment:she-was-cub-too', 'mentions', { status: 'confirmed' }),
  e('section:p95te', 'section:ex4uc', 'echoes', {
    gloss:
      'שני סקשנים בנויים מ־vignettes רחוב: ex4uc חמישה מצבים; p95te ארבע נפשות. אותה מתודה — צפייה אנושית כצורה ספרותית.',
  }),
  e('section:p95te', 'section:wjpfy', 'echoes', {
    gloss:
      '״פעם היא גם הייתה גורה״ של p95te + אסימטריית הקיום של wjpfy. שניהם מתעדים אנשים שעצירת הקיום שלהם הוכרעה לפני שנשאלו.',
  }),
  e('section:p95te', 'arc:tikkun-olam', 'in_arc'),

  // hhc3f ^זו טעות להאמין לאמנים
  e('section:hhc3f', 'moment:trust-failed-artists', 'mentions', { status: 'confirmed' }),
  e('section:hhc3f', 'section:34g10', 'echoes', {
    gloss:
      '34g10 אשמת הסטוריטלינג (בגידה באלמונים); hhc3f אשמת האמן (״הם נהנים מהכאב שלנו״). שתי גרסאות של אותה האשמה.',
  }),
  e('section:hhc3f', 'section:f4cwz', 'echoes', {
    gloss:
      'שני סקשני־אמן־לא־ראוי: f4cwz (״מלכים לא יכולים להיות נביאים״); hhc3f (״האמנים היחידים שאפשר להאמין להם הם הכושלים״). אותה תזה.',
  }),
  e('section:hhc3f', 'section:jdhee', 'echoes', {
    gloss:
      'jdhee ההודאה: ״אני זה שמלהק את עצמי״; hhc3f ההגדרה: אסור להאמין לאמנים כי הם נהנים. שניהם מטא־ביקורת על האמן.',
  }),
  e('section:hhc3f', 'arc:tikkun-olam', 'in_arc'),

  // vo8g6 ^רעב / חוסר
  e('section:vo8g6', 'person:shlomi', 'mentions', { status: 'confirmed' }),
  e('section:vo8g6', 'moment:starved-vs-lacking', 'mentions', { status: 'confirmed' }),
  e('section:vo8g6', 'moment:protect-from-revolt', 'mentions', { status: 'confirmed' }),
  e('section:vo8g6', 'section:p95te', 'echoes', {
    gloss: 'שני סקשני־צפייה־ברחוב: p95te שישי בבוקר; vo8g6 פועל ניקיון בסופה. שניהם מסתיימים בשאלה מי מגן על מי.',
  }),
  e('section:vo8g6', 'section:rlykw', 'echoes', {
    gloss: 'שני סקשני־שלומי על כתיבה: rlykw בפיסקה הגרועה; vo8g6 בתרגיל הכותרת. אותו פרשן.',
  }),
  e('section:vo8g6', 'arc:tikkun-olam', 'in_arc'),

  // k0ael ^אהה מומנט
  e('section:k0ael', 'moment:cleaners-bathroom', 'mentions', { status: 'confirmed' }),
  e('section:k0ael', 'section:ex4uc', 'echoes', {
    gloss:
      'מקבץ העובדים־הבלתי־נראים: ex4uc השוטף את חדר המדרגות; pg4q6 הסוללים בלילה; k0ael המנקות באפריקאים בקומה. שלוש גרסאות של אותה אסימטריה ביתית.',
  }),
  e('section:k0ael', 'section:pg4q6', 'echoes', {
    gloss: 'ראה ex4uc.',
  }),
  e('section:k0ael', 'arc:tikkun-olam', 'in_arc'),

  // --- Wave 13 edges ---

  // sixbu ^מכניס את הבטן
  e('section:sixbu', 'person:musk', 'mentions', { status: 'confirmed' }),
  e('section:sixbu', 'section:l5dr6', 'echoes', {
    gloss:
      'שני סקשני־מאסק: l5dr6 ת׳יל/מאסק כאידיאל מונופוליסטי; sixbu מאסק שמדכא סטודנט שעוקב אחרי המטוס שלו. שני כיוונים של אותה ביקורת.',
  }),
  e('section:sixbu', 'section:6zt53', 'echoes', {
    gloss:
      '״אני לא אוהב את הרעיון שאיזה מטורף יירה בי״ (מאסק ב־sixbu) ↔ ״ילכו רק על ראשי הפירמידה״ (6zt53). מאסק חושש בדיוק ממה ש־6zt53 מבטיח.',
  }),
  e('section:sixbu', 'arc:tikkun-olam', 'in_arc'),

  // 6gb39 ^פוסט פמיניזם ברחוב
  e('section:6gb39', 'section:3ycdx', 'echoes', {
    gloss:
      'שני סקשנים אנליטיים על מגדר אחרי המי־טו: 3ycdx ניתוח שחרור מיני וזנות; 6gb39 ביקורת על עיוורון של נשים במרחב הציבורי.',
  }),
  e('section:6gb39', 'arc:masculinity', 'in_arc'),

  // 7af3y ^סויפט בוומבלי
  e('section:7af3y', 'person:swift', 'mentions', { status: 'confirmed' }),
  e('section:7af3y', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:7af3y', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:7af3y', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:7af3y', 'section:bamju', 'echoes', {
    gloss: 'סוויפט מופיעה בעיתון על הגשר ב־bamju; כאן ב־7af3y במופע. שני קטבים: הסחה ציבורית מולנו ↔ מופע מולנו.',
  }),
  e('section:7af3y', 'section:l5dr6', 'echoes', {
    gloss: '״היא כמו מאסק, מכורה להערצה ולמניפולציות״ — סוויפט כסוג של מונופול. l5dr6 בעסקים, 7af3y בתרבות.',
  }),
  e('section:7af3y', 'arc:fatherhood', 'in_arc'),

  // hd9y9 ^בורחים לסבא וסבתא
  e('section:hd9y9', 'section:fh2np', 'echoes', {
    gloss:
      'מקבץ סקשני־יהדות: fh2np (השראה היסטורית), mk79t (משיח אתאיסט), 6beuw (תוכחה), hd9y9 (ויכוח עם אבא, ״לא יהודי דתי אבל מאיים״). ארבע גרסאות.',
  }),
  e('section:hd9y9', 'section:8bnsj', 'echoes', {
    gloss:
      '״הוא בחיים לא היה עובר. בכל מקום ישנאו אותנו״ של hd9y9 ↔ ״ערפדים״ של 8bnsj. שתי הזרויות־פנים — מהאבא ומהמשרד.',
  }),
  e('section:hd9y9', 'arc:homeland', 'in_arc'),

  // sd34h ^בסופר
  e('section:sd34h', 'section:ex4uc', 'echoes', {
    gloss:
      'משולש vignette־ים בתל אביב: ex4uc (חמישה מצבים), p95te (שישי בבוקר), sd34h (סופר + אוהב/לא־סובל את העיר). אותה מתודה — צפייה ארוכה.',
  }),
  e('section:sd34h', 'section:p95te', 'echoes', {
    gloss: 'ראה ex4uc.',
  }),
  e('section:sd34h', 'arc:tikkun-olam', 'in_arc'),

  // 7aynu ^עוד מישהו רוצה לשתף?
  e('section:7aynu', 'moment:my-country-changing', 'mentions', { status: 'confirmed' }),
  e('section:7aynu', 'section:dzrho', 'echoes', {
    gloss:
      'שלישית אוסטרליה: 7pxpc (הדרך), dzrho (סידני, בלו בר), 7aynu (חווה ליד סידני, מעגל הצופים). מסע אחד — שלושה רגעים של זרות־ביתיות.',
  }),
  e('section:7aynu', 'section:xfwea', 'echoes', {
    gloss:
      'שני סקשני־בושה־מהעם: xfwea ״העם הכי מטומטם״ (אקטיבי); 7aynu ״אני מתבייש נורא״ (פסיבי, מול זרים). שני קולות.',
  }),
  e('section:7aynu', 'arc:homeland', 'in_arc'),

  // b1t5r ^סימטה אלמונית
  e('section:b1t5r', 'moment:streets-anonymous', 'mentions', { status: 'confirmed' }),
  e('section:b1t5r', 'section:34g10', 'echoes', {
    gloss:
      'הדחיסה: 34g10 ביקורת על הסטוריטלינג שגונבת לאלמונים את הסיפור; b1t5r ההצעה האופרטיבית — הגרלה לרחובות על שמות אלמונים. תיאוריה + פתרון.',
  }),
  e('section:b1t5r', 'section:c1xqj', 'echoes', {
    gloss: 'c1xqj על ההכרה כאושר; b1t5r על שחרור מההכרה כמעשה אצילי. שתי גישות הפוכות.',
  }),
  e('section:b1t5r', 'section:p95te', 'echoes', {
    gloss: 'b1t5r ההצעה — רחובות על שם אלמונים, סיפורם על השלט; p95te הקבצנית, הזקן, הילדה — בדיוק האנשים האלה.',
  }),
  e('section:b1t5r', 'section:pg4q6', 'echoes', {
    gloss:
      'pg4q6 הסוללים את כבישי הרחובות שנקראים על שם גברים שזיינו להם את הנשים; b1t5r ההצעה ההפוכה — שהרחובות ייקראו על שמם. שני קטבים של אותו צדק־רחוב.',
  }),
  e('section:b1t5r', 'arc:tikkun-olam', 'in_arc'),

  // --- Wave 14 edges ---

  // 0ikrc ^מתארגנים לפיקניק
  e('section:0ikrc', 'person:dani', 'mentions', { status: 'confirmed' }),
  e('section:0ikrc', 'person:nelly', 'mentions', { status: 'confirmed' }),
  e('section:0ikrc', 'person:yaara', 'mentions', { status: 'confirmed' }),
  e('section:0ikrc', 'section:cmsiv', 'echoes', {
    gloss:
      'זוג סקשני־סצנה־ביתית: cmsiv שולחן האוכל (״לא יכול היה להיות סידור אחר״); 0ikrc הבוקר לפני פיקניק. שניהם רגעי־הורות יומיומיים, ללא דחיסה מטא — דווקא בזה כוחם.',
  }),
  e('section:0ikrc', 'arc:fatherhood', 'in_arc'),

  // 2vzzl ^ויזיט פלסטיין ודרך האמצע
  e('section:2vzzl', 'place:jericho', 'mentions', { status: 'confirmed' }),
  e('section:2vzzl', 'section:ciuso', 'echoes', {
    gloss:
      'שני סקשני־פעילות־שלום: ciuso התקווה לצעדה (״ענפי זית״); 2vzzl העשייה בפועל (סיורי ויזיט פלסטיין). תקווה + ביצוע.',
  }),
  e('section:2vzzl', 'section:9nez9', 'echoes', {
    gloss:
      '״הקיצונים משני הצדדים״ של לפיד שב־2vzzl תוקפת המספר — שדרך האמצע מבטלת את הניסיון הכן. 9nez9 ממשיך: אני יותר עם הקצה השני (ביביסטים) מאשר עם המרכז.',
  }),
  e('section:2vzzl', 'arc:pacifism', 'in_arc'),
  e('section:2vzzl', 'arc:homeland', 'in_arc'),

  // m6lah ^סלקציה עצמית
  e('section:m6lah', 'person:viktor-frankl', 'mentions', { status: 'confirmed' }),
  e('section:m6lah', 'source:viktor-frankl', 'mentions', { status: 'confirmed' }),
  e('section:m6lah', 'concept:gentle-perish', 'mentions', { status: 'confirmed' }),
  e('section:m6lah', 'moment:best-not-back', 'mentions', { status: 'confirmed' }),
  e('section:m6lah', 'place:auschwitz', 'mentions', { status: 'confirmed' }),
  e('section:m6lah', 'place:gaza', 'mentions', { status: 'confirmed' }),
  e('section:m6lah', 'section:wii5l', 'echoes', {
    gloss:
      'שני סקשני־שואה: wii5l הנר בכיתה (זיכרון יומיומי); m6lah פרנקל + אום בלסם (״הטובים לא חזרו״ + ״הם גונבים את הקמח״). השואה כעדות לאסימטריית הסבל.',
  }),
  e('section:m6lah', 'section:2f3gy', 'echoes', {
    gloss:
      '״הטובים שבנו לא חזרו״ הוא הראיה האמפירית לכך שהעדינים נכחדים — אקסיומת ההצדקה הנוספת לחמלה אוניברסלית של 2f3gy.',
  }),
  e('section:m6lah', 'arc:tikkun-olam', 'in_arc'),
  e('section:m6lah', 'arc:pacifism', 'in_arc'),

  // lw4rh ^אני חכם, אבל צריך לדייק
  e('section:lw4rh', 'concept:courage-as-intelligence', 'mentions', { status: 'confirmed' }),
  e('section:lw4rh', 'moment:courage-do-right', 'mentions', { status: 'confirmed' }),
  e('section:lw4rh', 'section:c1xqj', 'echoes', {
    gloss:
      '״אומץ הלב לעשות את הדבר הנכון״ ב־lw4rh ↔ ״מה שיבדיל בינינו הוא אומץ הלב, מידת ההקרבה״ ב־c1xqj. הגדרה־מחדש של אנושיות שמופיעה במצב אחד (כתיבה) ובמצב אחר (חידלון).',
  }),
  e('section:lw4rh', 'section:obmcs', 'echoes', {
    gloss:
      'שני סקשני־הערכה־עצמית: obmcs על משפט קצר; lw4rh על זיכרון וקריאה איטית. שניהם מודים בחסרון כדרך להגדיר־מחדש את הקריטריון.',
  }),
  e('section:lw4rh', 'arc:tikkun-olam', 'in_arc'),

  // yv95b ^למי אני כותב?
  e('section:yv95b', 'moment:write-grand', 'mentions', { status: 'confirmed' }),
  e('section:yv95b', 'section:9ij28', 'echoes', {
    gloss:
      '9ij28 על הקצב (״תקראו לאט״); yv95b על הקהל (״חייבים להאמין״). שני קולות פנימיים של הכתיבה — כל אחד מפתח עיקרון של אמונה.',
  }),
  e('section:yv95b', 'section:b9y06', 'echoes', {
    gloss:
      'שני סקשני־הכרזה־על־הספר: b9y06 ״התחלתי מהסוף״; yv95b ״הטקסט הזה נולד כדי לגעת במשהו נשגב״. הצהרות של שאיפה.',
  }),
  e('section:yv95b', 'section:5t3n9', 'echoes', {
    gloss: 'yv95b הצהרת־אמונה־בהצלחה־מראש; 5t3n9 הצלחה שהתרחשה (״לא מסתדר מתמטית״). שני זמני אותו רגע.',
  }),
  e('section:yv95b', 'arc:tikkun-olam', 'in_arc'),

  // 214hl ^פטריה עם עודד וחבר
  e('section:214hl', 'person:oded', 'mentions', { status: 'confirmed' }),
  e('section:214hl', 'section:hhsas', 'echoes', {
    gloss:
      'זוג סקשני־טבעונות: hhsas על האש (״המוות של השעות״ והבשר); 214hl פטריה ופיצה (הרגע שמסיים בין־לבין). השני הוא הביצוע של הראשון.',
  }),
  e('section:214hl', 'section:vy99k', 'echoes', {
    gloss:
      'אסימטריית הסבל מ־vy99k (״התרגשות הדייג + בעתה הדג = מספר שלילי״) מקבלת ב־214hl ניסוח מעשי: ״התודעה שיצאה הגבינה מדברת איתי״.',
  }),
  e('section:214hl', 'section:2f3gy', 'echoes', {
    gloss: 'החמלה האוניברסלית של 2f3gy מתפרשת ב־214hl פרגמטית: צמחונות → טבעונות. שלב באקסיומה.',
  }),
  e('section:214hl', 'arc:pacifism', 'in_arc'),
  e('section:214hl', 'arc:tikkun-olam', 'in_arc'),

  // g1is2 ^הקדמה למהדורה הסינית
  e('section:g1is2', 'place:china', 'mentions', { status: 'confirmed' }),
  e('section:g1is2', 'moment:non-suffered-people', 'mentions', { status: 'confirmed' }),
  e('section:g1is2', 'section:5t3n9', 'echoes', {
    gloss:
      'צ׳נגדו חוזרת! 5t3n9 הסטודנטית מצ׳נגדו שפנתה לגבי תרגום; g1is2 רגע התהלוכה הסינית של הנידונים למוות. שני קצוות של אותו טיול — הצלחת הטקסט והסבל הקולקטיבי שהוא בא לדבר עליו.',
  }),
  e('section:g1is2', 'section:2f3gy', 'echoes', {
    gloss: '״יש עם שלא עבר זוועה?״ של g1is2 = ההצדקה הגלובלית של החמלה האוניברסלית. החמלה לא יכולה להיות סקטוריאלית.',
  }),
  e('section:g1is2', 'section:432rb', 'echoes', {
    gloss: 'הפירמידה של 432rb מתבטאת כאן כפיזית: התהלוכה הציבורית עם הנידונים למוות = מבני־כוח שמעצבים את תודעת כולם.',
  }),
  e('section:g1is2', 'arc:tikkun-olam', 'in_arc'),

  // --- Wave 15 (final) edges ---

  // zf7ql ^לנין
  e('section:zf7ql', 'person:lenin', 'mentions', { status: 'confirmed' }),
  e('section:zf7ql', 'source:lenin-quote', 'mentions', { status: 'confirmed' }),
  e('section:zf7ql', 'moment:communists-for-fundraising', 'mentions', { status: 'confirmed' }),
  e('section:zf7ql', 'section:l5dr6', 'echoes', {
    gloss:
      'הסתירה הקפיטליסטית: l5dr6 ת׳יל על ״מונופולים מביאים לקידמה״; zf7ql יזם משתמש בלנין כדי לגייס כסף ממשקיעים. השימוש הסובברסיבי בהיפוכים אידיאולוגיים.',
  }),
  e('section:zf7ql', 'section:vns47', 'echoes', {
    gloss:
      'שני סקשני־מניפסט־קומוניסטי: vns47 ״רוח בלהות מהלכת על פני אירופה״ כבאג; zf7ql לנין כפתיחה לפיץ׳. המניפסט הקומוניסטי כמשאב שיווקי.',
  }),
  e('section:zf7ql', 'arc:tikkun-olam', 'in_arc'),

  // 8kjcg ^בקונקשן בפרנקפורט
  e('section:8kjcg', 'place:argentina', 'mentions', { status: 'confirmed' }),
  e('section:8kjcg', 'place:buenos-aires', 'mentions', { status: 'confirmed' }),
  e('section:8kjcg', 'section:7pxpc', 'echoes', {
    gloss: 'שני סקשני־קונקשן: 7pxpc הדרך לסידני; 8kjcg הקונקשן לבואנוס. שני שדות־תעופה כסיטואציות־של־התבוננות־פנימה.',
  }),
  e('section:8kjcg', 'section:214hl', 'echoes', {
    gloss:
      'הצמחונות של 214hl פוגשת ב־8kjcg את ארגנטינה־של־הבשר. ״ישראל מספר אחת בצריכת עופות, בשר זה ארגנטינאים״. החזרה של המוסר הצמחוני בעוצמה.',
  }),
  e('section:8kjcg', 'arc:homeland', 'in_arc'),
  e('section:8kjcg', 'arc:pacifism', 'in_arc'),

  // wrrph ^קונה בורג
  e('section:wrrph', 'moment:cant-price-yourself', 'mentions', { status: 'confirmed' }),
  e('section:wrrph', 'section:l5dr6', 'echoes', {
    gloss:
      'l5dr6 על המונופוליסט שגובה את המקסימום; wrrph על בעל החנות שלא מצליח לתמחר את עצמו. שני קצוות של ספקטרום ההון.',
  }),
  e('section:wrrph', 'section:b1t5r', 'echoes', {
    gloss:
      'מקבץ אצילות־של־אלמונים: b1t5r הצעת רחובות לאלמונים; wrrph בעל החנות שאינו יודע לתמחר את עצמו. שני סקשנים שגוזרים אצילות מהקצוות.',
  }),
  e('section:wrrph', 'section:p95te', 'echoes', {
    gloss: 'wrrph שיחה ארוכה עם בעל חנות; p95te vignettes רחוב. שניהם מתעדים את האזרח־הפשוט כעוגן של הטקסט.',
  }),
  e('section:wrrph', 'arc:tikkun-olam', 'in_arc'),
]

// --- Section → SourceFile mapping ---

const SOURCE_FILE_BY_SOURCE_NAME: Record<string, string> = {
  אני: 'source-file:ani',
  'ג׳קי-וזכרונות-ילדות': 'source-file:jackie',
  גבריות: 'source-file:masculinity',
  'הורות,-ילדות': 'source-file:parenting',
  חברים: 'source-file:friends',
  פסיכולוג: 'source-file:psychologist',
  'משרד,-עבודה,-קריירה': 'source-file:office',
  כתיבה: 'source-file:writing',
  הנדסה: 'source-file:engineering',
  טבעונות: 'source-file:vegetarianism',
  'החברה-הישראלית': 'source-file:israeli-society',
  'פניה-לאחרים': 'source-file:address',
  'המניפסט-הגלוי': 'source-file:manifesto-visible',
  'המניפסט-האחרון': 'source-file:manifesto-encrypted',
}

for (const node of nodes) {
  if (node.type === 'Section') {
    const sourceName = node.attrs?.source
    if (sourceName) {
      const sourceFileId = SOURCE_FILE_BY_SOURCE_NAME[sourceName]
      if (sourceFileId) {
        edges.push(e(node.id, sourceFileId, 'in_source', { status: 'confirmed' }))
      }
    }
  }
}

// --- Stage assignments ---

export const SECTION_STAGE: Record<string, Stage> = {
  // Childhood
  'section:vns47': 'childhood',
  'section:uwe6h': 'childhood',
  'section:5l706': 'childhood',

  // Adolescence, army, studies
  'section:nfzy3': 'adolescence-army-studies',

  // Mitzpe & Yaara
  'section:ex4uc': 'mitzpe-yaara',
  'section:k4sm6': 'mitzpe-yaara',
  'section:2m1t5': 'mitzpe-yaara',
  'section:kbstp': 'mitzpe-yaara',
  'section:k90lu': 'mitzpe-yaara',
  'section:2vzzl': 'mitzpe-yaara',
  'section:cmsiv': 'mitzpe-yaara',
  'section:0ikrc': 'mitzpe-yaara',
  'section:wii5l': 'mitzpe-yaara',
  'section:b1t5r': 'mitzpe-yaara',
  'section:zf7ql': 'mitzpe-yaara',
  'section:adkli': 'mitzpe-yaara',
  'section:0sil4': 'mitzpe-yaara',
  'section:p95te': 'mitzpe-yaara',

  // Opening
  'section:9smx7': 'opening',
  'section:vy99k': 'opening',
  'section:o3scq': 'opening',
  'section:0vjhk': 'opening',
  'section:o5fsv': 'opening',
  'section:0k1m6': 'opening',
  'section:1z89o': 'opening',
  'section:p93pj': 'opening',
  'section:3ycdx': 'opening',
  'section:3rcmm': 'opening',
  'section:f0gcr': 'opening',
  'section:rlykw': 'opening',
  'section:6gb39': 'opening',
  'section:j0p47': 'opening',
  'section:qjxw2': 'opening',
  'section:8t1ae': 'opening',
  'section:7af3y': 'opening',
  'section:hftnk': 'opening',
  'section:y6rx4': 'opening',
  'section:z95et': 'opening',
  'section:6zt53': 'opening',
  'section:pg4q6': 'opening',
  'section:214hl': 'opening',
  'section:fvrmk': 'opening',
  'section:vq9l7': 'opening',
  'section:8bnsj': 'opening',
  'section:k0ael': 'opening',
  'section:hhsas': 'opening',
  'section:7pxpc': 'opening',
  'section:dzrho': 'opening',
  'section:7aynu': 'opening',
  'section:g1is2': 'opening',
  'section:8kjcg': 'opening',
  'section:sd34h': 'opening',
  'section:9nez9': 'opening',

  // Synthesis
  'section:sixbu': 'synthesis',
  'section:cnx02': 'synthesis',
  'section:f4cwz': 'synthesis',
  'section:shg1c': 'synthesis',
  'section:6n3p1': 'synthesis',
  'section:6qpg1': 'synthesis',
  'section:sae6e': 'synthesis',
  'section:g1n8e': 'synthesis',
  'section:1tye1': 'synthesis',
  'section:v37cy': 'synthesis',
  'section:odokn': 'synthesis',
  'section:c1xqj': 'synthesis',
  'section:fh2np': 'synthesis',
  'section:mk79t': 'synthesis',
  'section:6beuw': 'synthesis',
  'section:th813': 'synthesis',
  'section:m6lah': 'synthesis',
  'section:ciuso': 'synthesis',
  'section:hd9y9': 'synthesis',
  'section:xfwea': 'synthesis',
  'section:vdugi': 'synthesis',
  'section:xwgu8': 'synthesis',
  'section:z58pz': 'synthesis',
  'section:2f3gy': 'synthesis',
  'section:l5dr6': 'synthesis',
  'section:pzxl2': 'synthesis',
  'section:wjpfy': 'synthesis',
  'section:432rb': 'synthesis',
  'section:kql06': 'synthesis',
  'section:qrd6i': 'synthesis',
  'section:hhc3f': 'synthesis',
  'section:wrrph': 'synthesis',
  'section:igclx': 'synthesis',
  'section:zza3z': 'synthesis',

  // Meta — out of biographical time
  'section:el0pb': 'meta',
  'section:5t3n9': 'meta',
  'section:eu99q': 'meta',
  'section:lvq6c': 'meta',
  'section:bamju': 'meta',
  'section:obmcs': 'meta',
  'section:fjggr': 'meta',
  'section:hiw9o': 'meta',
  'section:c1s75': 'meta',
  'section:mp2l0': 'meta',
  'section:jdhee': 'meta',
  'section:ipv31': 'meta',
  'section:pl881': 'meta',
  'section:rb87n': 'meta',
  'section:9ij28': 'meta',
  'section:34g10': 'meta',
  'section:26n6m': 'meta',
  'section:b9y06': 'meta',
  'section:vkcgj': 'meta',
  'section:vo8g6': 'meta',
  'section:lw4rh': 'meta',
  'section:yv95b': 'meta',
}

export const graphData = { nodes, edges }
